import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildSurveyTeamSlotIso,
  parseSurveyTeamScheduledSlots,
  surveyTeamSlotsOnDate,
  strapiEnsureSamplingScheduleForQuotation,
  strapiListSamplingPlansForQuotation,
} from '../../../../api/strapiCrm';
import type { StrapiSamplingScheduleRow } from '../../../../api/strapiCrm';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import {
  freelanceMemberKey,
  freelanceDocumentIdFromMemberKey,
  isFreelanceMemberKey,
  strapiCreateFreelanceUser,
  strapiListFreelanceUsers,
  type StrapiFreelanceUserRow,
} from '../../../../api/strapiFreelanceUsers';
import type { StrapiManagedUserRow } from '../../../../api/strapiUsers';
import { strapiListUserTypes } from '../../../../api/strapiUserTypes';
import {
  MonthlyQuotationTeamCalendar,
  QuotationScheduleDayDetailDialog,
  QuotationScheduleSlotAssignDialog,
  SurveyScheduleCalendarLegend,
  SurveyScheduleCalendarToolbar,
  applyBeritaAcaraIssuedFlag,
  buildQuotationScheduleDaySummariesByDate,
  dynamicRelation,
  isActiveScheduleOnDetailCalendar,
  scheduleDocIdFromTeamRow,
  surveyTeamAvailabilityForUsers,
  type QuotationScheduleDaySummary,
  type SlotAssignConfirmPayload,
  type SlotAssignScheduleNotes,
  type SlotAssignWorkOrderPayload,
} from '../../../../components/surveyScheduleCalendar';
import { isAdminSampling } from '../../../QuotationsPage/quotationWorkflow';
import { useIssuedBeritaAcaraWorkOrderIds, useSurveyTeamsScheduleQuery, useUsersQuery } from '../../../../query/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../../query/queryKeys';
import {
  buildSlotsAdoptingTeamOnSingleDate,
  moveDateInSlotsMap,
  removeDateFromSlotsMap,
} from './scheduleCardMove';
import {
  buildGroupLabelForSchedule,
  buildUnscheduledSampleGroupOptions,
  contractSampleGroupOptionForSchedule,
  collectPlanDocumentIdsFromPayloads,
  sampleGroupOptionSelectionKey,
  quotationGroupIndexFromPlans,
  type QuotationScheduleGroupMeta,
} from '../../samplingSchedulePlans';
import {
  canCreateWorkOrderFromSchedule,
  canEditSamplingScheduleDetail,
  canEditScheduleTransportasiNote,
  workOrderNewPathFromSchedule,
} from '../../samplingScheduleAccess';
import { buildDetailPageScheduleCalendar, customerAgreedCalendarDatesFromSchedule } from '../../samplingScheduleCalendarModel';
import { useAuth } from '../../../../auth/AuthContext';
import {
  currentMonthAnchor,
  isoDatesInclusive,
  defaultSurveyRangeFromSchedule,
  weekDatesForwardFrom,
} from '../../../../sampling/surveyScheduleCalendar';

export type ScheduleTeamMemberDraft = {
  draftKey: string;
  user: StrapiManagedUserRow | null;
  freelanceUser: StrapiFreelanceUserRow | null;
  is_lead: boolean;
};

export type ScheduleTeamSaveOptions = {
  stayOnPage?: boolean;
  allowEmptyTeam?: boolean;
  quotationGroupDocumentId?: string;
  quotationRecurringEventDocumentId?: string;
  /** Hapus/ubah jadwal pelanggan lain pada quotation yang sama, tanpa pindah halaman. */
  targetScheduleDocumentId?: string;
  /**
   * Jadwal lain di card tim yang sama: roster diganti dengan anggota yang sama,
   * tanggal mengikuti slot yang sudah ada di masing-masing jadwal.
   */
  propagateRosterToScheduleDocumentIds?: string[];
  /**
   * Edit petugas tim: ganti roster hanya pada tanggal ini untuk quotation/jadwal target.
   * Tanggal lain dan quotation lain tidak dihapus.
   */
  replaceRosterOnDate?: string;
  /** Admin WO: promote schedule ke work order setelah tanggal tersimpan. */
  workOrderCreate?: SlotAssignWorkOrderPayload;
  /** Nama tim per tanggal (`YYYY-MM-DD` → nama). */
  teamNamesByDate?: Record<string, string> | null;
  /** @deprecated Gunakan teamNamesByDate */
  teamName?: string | null;
  /**
   * Salin roster ke tanggal yang belum punya petugas.
   * Tidak menghapus baris `survey_teams` yang sudah ada.
   */
  appendRoster?: ScheduleAppendRoster | null;
};

export type ScheduleAppendRosterMember = {
  user: StrapiManagedUserRow | null;
  freelanceUser: StrapiFreelanceUserRow | null;
  isLead: boolean;
  time: string;
};

export type ScheduleAppendRoster = {
  dates: string[];
  teamName: string | null;
  members: ScheduleAppendRosterMember[];
};

type ScheduleRosterMember = ScheduleAppendRosterMember & { memberKey: string };

type CopiedWeekRoster = {
  dates: string[];
  teamName: string | null;
  members: ScheduleRosterMember[];
};

function draftMemberKey(draft: ScheduleTeamMemberDraft): string | null {
  if (draft.user) return String(draft.user.id);
  if (draft.freelanceUser?.documentId) return freelanceMemberKey(draft.freelanceUser.documentId);
  return null;
}

function draftMemberLabel(draft: ScheduleTeamMemberDraft): string {
  if (draft.user) return userDisplayName(draft.user);
  const name = draft.freelanceUser?.name?.trim();
  return name ? `${name} (freelance)` : 'Freelance';
}

function userDisplayName(user: StrapiManagedUserRow): string {
  return user.username?.trim() || user.email || user.id;
}

function collectTeamNamesByDateFromRows(
  teamRows: DynamicEntityRow[],
  scheduleDocumentId: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const team of teamRows) {
    const teamScheduleId = scheduleDocIdFromTeamRow(team);
    if (teamScheduleId && teamScheduleId !== scheduleDocumentId) continue;
    const name = String(team.team_name ?? '').trim();
    if (!name) continue;
    for (const slot of parseSurveyTeamScheduledSlots(team.scheduled_date)) {
      if (!out[slot.date]) out[slot.date] = name;
    }
  }
  return out;
}

function collectTeamDraftStateFromRows(
  teamRows: DynamicEntityRow[],
  scheduleDocumentId: string,
  users: StrapiManagedUserRow[],
  freelanceUsers: StrapiFreelanceUserRow[],
): { drafts: ScheduleTeamMemberDraft[]; slotsByUserId: Map<string, string[]> } {
  const byMember = new Map<
    string,
    {
      user: StrapiManagedUserRow | null;
      freelanceUser: StrapiFreelanceUserRow | null;
      is_lead: boolean;
      slots: string[];
    }
  >();

  for (const team of teamRows) {
    const teamScheduleId = scheduleDocIdFromTeamRow(team);
    if (teamScheduleId && teamScheduleId !== scheduleDocumentId) continue;

    const freelanceRec = dynamicRelation(team, 'freelance_user');
    const freelanceDocId = String(freelanceRec?.documentId ?? '').trim();
    if (freelanceDocId) {
      const fromList = freelanceUsers.find((row) => row.documentId === freelanceDocId) ?? null;
      const freelanceUser: StrapiFreelanceUserRow =
        fromList ??
        ({
          documentId: freelanceDocId,
          numericId: Number.isFinite(Number(freelanceRec?.id)) ? Number(freelanceRec?.id) : null,
          name: String(freelanceRec?.name ?? '').trim(),
          phone_number: String(freelanceRec?.phone_number ?? '').trim(),
          active_status: String(freelanceRec?.active_status ?? 'active'),
        } satisfies StrapiFreelanceUserRow);
      const memberKey = freelanceMemberKey(freelanceDocId);
      const slots = parseSurveyTeamScheduledSlots(team.scheduled_date).map((s) => s.raw);
      const existing = byMember.get(memberKey);
      if (existing) {
        existing.slots.push(...slots);
        existing.is_lead = existing.is_lead || Boolean(team.is_lead);
      } else {
        byMember.set(memberKey, {
          user: null,
          freelanceUser,
          is_lead: Boolean(team.is_lead),
          slots,
        });
      }
      continue;
    }

    const userRec = dynamicRelation(team, 'user_idx');
    const match = managedUserFromTeamRelation(userRec, users);
    const userKey = match?.id
      ? String(match.id)
      : String(userRec?.documentId ?? '').trim();
    if (!userKey) continue;

    const slots = parseSurveyTeamScheduledSlots(team.scheduled_date).map((s) => s.raw);
    const existing = byMember.get(userKey);
    if (existing) {
      existing.slots.push(...slots);
      existing.is_lead = existing.is_lead || Boolean(team.is_lead);
    } else {
      byMember.set(userKey, {
        user: match,
        freelanceUser: null,
        is_lead: Boolean(team.is_lead),
        slots,
      });
    }
  }

  const drafts: ScheduleTeamMemberDraft[] = [...byMember.entries()].map(([key, row], idx) => ({
    draftKey: `loaded-${key}-${idx}`,
    user: row.user,
    freelanceUser: row.freelanceUser,
    is_lead: row.is_lead,
  }));
  if (drafts.length > 0 && !drafts.some((d) => d.is_lead)) {
    drafts[0] = { ...drafts[0], is_lead: true };
  }

  const slotsByUserId = new Map<string, string[]>();
  for (const [key, row] of byMember.entries()) {
    slotsByUserId.set(String(key), [...new Set(row.slots)].sort());
  }
  return { drafts, slotsByUserId };
}

function quotationGroupIdForSchedule(
  schedules: StrapiSamplingScheduleRow[],
  teams: DynamicEntityRow[],
  scheduleDocumentId: string,
): string {
  const fromSchedule = String(
    schedules.find((row) => row.documentId === scheduleDocumentId)?.quotation_group?.documentId ?? '',
  ).trim();
  if (fromSchedule) return fromSchedule;
  for (const team of teams) {
    if (scheduleDocIdFromTeamRow(team) !== scheduleDocumentId) continue;
    const scheduleRel = dynamicRelation(team, 'sampling_schedule');
    const group = scheduleRel
      ? dynamicRelation(scheduleRel as DynamicEntityRow, 'quotation_group')
      : null;
    const id = String(group?.documentId ?? '').trim();
    if (id) return id;
  }
  return '';
}

function scheduleDateHideKey(scheduleDocumentId: string, date: string): string {
  return `${scheduleDocumentId.trim()}|${date}`;
}

function omitHiddenScheduleDatesFromTeams(
  teams: DynamicEntityRow[],
  hidden: ReadonlySet<string>,
): DynamicEntityRow[] {
  if (hidden.size === 0) return teams;
  const out: DynamicEntityRow[] = [];
  for (const row of teams) {
    const scheduleId = scheduleDocIdFromTeamRow(row);
    if (!scheduleId) {
      out.push(row);
      continue;
    }
    const slots = parseSurveyTeamScheduledSlots(row.scheduled_date);
    const kept = slots
      .filter((slot) => !hidden.has(scheduleDateHideKey(scheduleId, slot.date)))
      .map((slot) => slot.raw);
    if (kept.length === slots.length) {
      out.push(row);
      continue;
    }
    if (kept.length === 0) continue;
    out.push({ ...row, scheduled_date: kept });
  }
  return out;
}

function omitHiddenScheduleDatesFromSummaries(
  summariesByDate: Map<string, QuotationScheduleDaySummary[]>,
  hidden: ReadonlySet<string>,
): Map<string, QuotationScheduleDaySummary[]> {
  if (hidden.size === 0) return summariesByDate;
  const next = new Map<string, QuotationScheduleDaySummary[]>();
  for (const [date, rows] of summariesByDate) {
    const filtered = rows.filter(
      (row) => !hidden.has(scheduleDateHideKey(row.scheduleDocumentId, date)),
    );
    if (filtered.length > 0) next.set(date, filtered);
  }
  return next;
}

function scheduleDateStillOnServer(
  scheduleId: string,
  date: string,
  teamRows: DynamicEntityRow[],
  schedules: StrapiSamplingScheduleRow[],
): boolean {
  for (const row of teamRows) {
    if (scheduleDocIdFromTeamRow(row) !== scheduleId) continue;
    if (parseSurveyTeamScheduledSlots(row.scheduled_date).some((slot) => slot.date === date)) {
      return true;
    }
  }
  const schedule = schedules.find((row) => row.documentId === scheduleId);
  if (!schedule) return false;
  return customerAgreedCalendarDatesFromSchedule(schedule).includes(date);
}

function remainingDateOnlySlotsForSchedule(
  extraByDate: Map<string, QuotationScheduleDaySummary[]>,
  schedules: StrapiSamplingScheduleRow[],
  scheduleDocumentId: string,
  removeDate: string,
): string[] {
  const fromExtra = [...extraByDate.entries()]
    .filter(([, rows]) => rows.some((row) => row.scheduleDocumentId === scheduleDocumentId))
    .map(([date]) => date)
    .filter((date) => date !== removeDate)
    .sort();
  if (fromExtra.length > 0) return fromExtra;

  const hadDeletedDate = [...extraByDate.entries()].some(
    ([date, rows]) =>
      date === removeDate && rows.some((row) => row.scheduleDocumentId === scheduleDocumentId),
  );
  if (hadDeletedDate) return [];

  const row = schedules.find((schedule) => schedule.documentId === scheduleDocumentId);
  if (!row) return [];
  return customerAgreedCalendarDatesFromSchedule(row).filter((date) => date !== removeDate);
}

type ScheduleEditMode = 'readonly' | 'date_only' | 'full';

type Props = {
  jwt: string | null;
  scheduleDocumentId: string;
  scheduleNumber?: string | null;
  scheduleStatus?: string | null;
  adminWorkorderCompletedAt?: string | null;
  adminSamplingCompletedAt?: string | null;
  samplingReceivedAt?: string | null;
  quotationDocumentId?: string | null;
  quotationNumber?: string | null;
  quotationUsesGroups?: boolean;
  quotationGroupMeta?: QuotationScheduleGroupMeta;
  scheduleQuotationGroup?: StrapiSamplingScheduleRow['quotation_group'] | null;
  recurringEventIndex?: number | null;
  recurringEventDocumentId?: string | null;
  customerName?: string | null;
  customerAddress?: string | null;
  scheduleNotes?: string | null;
  scheduleTransportasiNote?: string | null;
  workOrderDocumentId?: string | null;
  workOrderJobNumber?: string | null;
  anchorDate?: string | null;
  anchorEndDate?: string | null;
  /** Tanggal kesepakatan eksplisit dari schedule (`customer_agreed_dates`). */
  customerAgreedDates?: string[] | null;
  locked: boolean;
  editMode?: ScheduleEditMode;
  teams: DynamicEntityRow[];
  /** Daftar penjadwalan untuk metadata kalender (selaras dengan halaman list). */
  allSchedulesForCalendar?: StrapiSamplingScheduleRow[];
  users: StrapiManagedUserRow[];
  usersLoading: boolean;
  saving: boolean;
  teamSaveTick?: number;
  onSave: (
    members: ScheduleTeamMemberDraft[],
    slotsByUserId: Map<string, string[]>,
    notes: string,
    planDocumentIds?: string[],
    dateOnlySlots?: string[],
    transportasiNote?: string,
    options?: ScheduleTeamSaveOptions,
  ) => void;
  /** Hapus satu tanggal dari survey_team di server (update/delete in-place). */
  onRemoveScheduleDate?: (args: {
    date: string;
    scheduleDocumentId: string;
    notes: string;
    transportasiNote: string;
  }) => void | Promise<void>;
};

function managedUserFromTeamRelation(
  userRec: Record<string, unknown> | null,
  users: StrapiManagedUserRow[],
): StrapiManagedUserRow | null {
  if (!userRec) return null;
  const docId = String(userRec.documentId ?? '').trim();
  const numericId = Number(userRec.id);
  const fromList =
    users.find((u) => u.documentId === docId) ??
    users.find((u) => u.numericId === numericId) ??
    null;
  if (fromList) return fromList;
  if (!docId && !Number.isFinite(numericId)) return null;
  const id = Number.isFinite(numericId) ? String(numericId) : docId;
  return {
    id,
    documentId: docId || null,
    numericId: Number.isFinite(numericId) ? numericId : null,
    username: String(userRec.username ?? ''),
    email: String(userRec.email ?? ''),
    blocked: false,
    userTypeCode: null,
    userTypeName: null,
  };
}

function assignedDatesForSchedule(
  slotsByUserId: Map<string, string[]>,
  teamRows: DynamicEntityRow[],
  scheduleDocumentId: string,
): Set<string> {
  const out = new Set<string>();
  for (const rawList of slotsByUserId.values()) {
    for (const parsed of parseSurveyTeamScheduledSlots(rawList)) out.add(parsed.date);
  }
  for (const team of teamRows) {
    const teamScheduleId = scheduleDocIdFromTeamRow(team);
    if (teamScheduleId && teamScheduleId !== scheduleDocumentId) continue;
    for (const slot of parseSurveyTeamScheduledSlots(team.scheduled_date)) out.add(slot.date);
  }
  return out;
}

/** Petugas pada satu tanggal jadwal: user/freelance, jam, lead, dan nama tim. */
function rosterOnScheduleDate(
  teamRows: DynamicEntityRow[],
  scheduleDocumentId: string,
  date: string,
  users: StrapiManagedUserRow[],
  freelanceUsers: StrapiFreelanceUserRow[],
): { teamName: string | null; members: ScheduleRosterMember[] } | null {
  const members: ScheduleRosterMember[] = [];
  const seen = new Set<string>();
  let teamName: string | null = null;

  for (const team of teamRows) {
    const teamScheduleId = scheduleDocIdFromTeamRow(team);
    if (teamScheduleId && teamScheduleId !== scheduleDocumentId) continue;
    const daySlots = surveyTeamSlotsOnDate(team, date);
    if (daySlots.length === 0) continue;
    const time =
      daySlots.map((slot) => slot.timeLabel).find((label) => /^\d{2}:\d{2}$/.test(label)) ?? '08:00';
    const name = String(team.team_name ?? '').trim();
    if (name && !teamName) teamName = name;

    const freelanceRec = dynamicRelation(team, 'freelance_user');
    const freelanceDocId = String(freelanceRec?.documentId ?? '').trim();
    if (freelanceDocId) {
      const memberKey = freelanceMemberKey(freelanceDocId);
      if (seen.has(memberKey)) continue;
      seen.add(memberKey);
      const fromList = freelanceUsers.find((row) => row.documentId === freelanceDocId) ?? null;
      const freelanceUser: StrapiFreelanceUserRow =
        fromList ??
        ({
          documentId: freelanceDocId,
          numericId: Number.isFinite(Number(freelanceRec?.id)) ? Number(freelanceRec?.id) : null,
          name: String(freelanceRec?.name ?? '').trim(),
          phone_number: String(freelanceRec?.phone_number ?? '').trim(),
          active_status: String(freelanceRec?.active_status ?? 'active'),
        } satisfies StrapiFreelanceUserRow);
      members.push({
        memberKey,
        user: null,
        freelanceUser,
        isLead: Boolean(team.is_lead),
        time,
      });
      continue;
    }

    const userRec = dynamicRelation(team, 'user_idx');
    const match = managedUserFromTeamRelation(userRec, users);
    if (!match?.id) continue;
    const memberKey = String(match.id);
    if (seen.has(memberKey)) continue;
    seen.add(memberKey);
    members.push({
      memberKey,
      user: match,
      freelanceUser: null,
      isLead: Boolean(team.is_lead),
      time,
    });
  }

  if (members.length === 0) return null;
  if (!members.some((member) => member.isLead)) {
    members[0] = { ...members[0]!, isLead: true };
  } else {
    let leadAssigned = false;
    for (let index = 0; index < members.length; index += 1) {
      const isLead = Boolean(members[index]!.isLead) && !leadAssigned;
      members[index] = { ...members[index]!, isLead };
      if (isLead) leadAssigned = true;
    }
  }
  return { teamName, members };
}

/**
 * Roster pada tanggal paling akhir di jadwal ini, untuk tanggal terpilih yang masih kosong.
 * Tanggal yang sudah punya petugas tidak diisi. Jadwal tanpa tim survey tidak disalin.
 */
function copiedRosterForEmptyScheduleDates(opts: {
  teamRows: DynamicEntityRow[];
  scheduleDocumentId: string;
  selectedDates: string[];
  occupiedDates: Set<string>;
  users: StrapiManagedUserRow[];
  freelanceUsers: StrapiFreelanceUserRow[];
}): CopiedWeekRoster | null {
  const datesOnSchedule = new Set<string>();
  for (const team of opts.teamRows) {
    const teamScheduleId = scheduleDocIdFromTeamRow(team);
    if (teamScheduleId && teamScheduleId !== opts.scheduleDocumentId) continue;
    for (const slot of parseSurveyTeamScheduledSlots(team.scheduled_date)) {
      datesOnSchedule.add(slot.date);
    }
  }
  let roster: ReturnType<typeof rosterOnScheduleDate> = null;
  for (const date of [...datesOnSchedule].sort().reverse()) {
    const onDate = rosterOnScheduleDate(
      opts.teamRows,
      opts.scheduleDocumentId,
      date,
      opts.users,
      opts.freelanceUsers,
    );
    if (onDate) {
      roster = onDate;
      break;
    }
  }
  if (!roster || roster.members.length === 0) return null;
  const dates = opts.selectedDates.filter((date) => !opts.occupiedDates.has(date));
  if (dates.length === 0) return null;
  return { dates, teamName: roster.teamName, members: roster.members };
}

type ScheduleCalendarMeta = {
  scheduleNumber?: string | null;
  scheduleStatus?: string | null;
  quotationDocumentId?: string | null;
  quotationNumber?: string | null;
  customerName?: string | null;
  customerAddress?: string | null;
  scheduleNotes?: string | null;
  scheduleTransportasiNote?: string | null;
  measurementTypesLabel?: string | null;
  workOrderDocumentId?: string | null;
  workOrderJobNumber?: string | null;
  quotationGroupMeta?: QuotationScheduleGroupMeta;
  scheduleQuotationGroup?: StrapiSamplingScheduleRow['quotation_group'] | null;
  recurringEventIndex?: number | null;
  linkedSamplingPlans?: DynamicEntityRow[];
};

function buildSyntheticSamplingSchedule(
  scheduleDocumentId: string,
  scheduleMeta?: ScheduleCalendarMeta,
): Record<string, unknown> {
  const addressParts = (scheduleMeta?.customerAddress ?? '').split(',').map((s) => s.trim());
  const groupMeta = scheduleMeta?.quotationGroupMeta;
  const workOrderDocumentId = String(scheduleMeta?.workOrderDocumentId ?? '').trim();
  const workOrderJobNumber = String(scheduleMeta?.workOrderJobNumber ?? '').trim();
  return {
    documentId: scheduleDocumentId,
    schedule_number: scheduleMeta?.scheduleNumber ?? '',
    progress_status: scheduleMeta?.scheduleStatus ?? '',
    notes: scheduleMeta?.scheduleNotes ?? '',
    transportasi_note: scheduleMeta?.scheduleTransportasiNote ?? '',
    measurement_types_label: scheduleMeta?.measurementTypesLabel ?? '',
    quotation_group: scheduleMeta?.scheduleQuotationGroup ?? null,
    sampling_plans: scheduleMeta?.linkedSamplingPlans ?? [],
    work_order: workOrderDocumentId
      ? {
          documentId: workOrderDocumentId,
          job_number: workOrderJobNumber,
        }
      : null,
    quotation_recurring_event:
      scheduleMeta?.recurringEventIndex != null
        ? { event_index: scheduleMeta.recurringEventIndex }
        : null,
    quotation: {
      documentId: scheduleMeta?.quotationDocumentId ?? '',
      quotation_number: scheduleMeta?.quotationNumber ?? '',
      is_contract: groupMeta?.is_contract ?? false,
      is_consultant_quote: groupMeta?.is_consultant_quote ?? false,
      contract_period_start_date: groupMeta?.contract_period_start_date ?? null,
      contract_period_end_date: groupMeta?.contract_period_end_date ?? null,
      customer: {
        name: scheduleMeta?.customerName ?? '',
        address: addressParts[0] ?? '',
        city: addressParts[1] ?? '',
        province: addressParts[2] ?? '',
      },
    },
  };
}

function measurementTypesLabelFromPlans(plans: DynamicEntityRow[]): string {
  const labels = new Set<string>();
  plans.forEach((plan) => {
    const line = dynamicRelation(plan, 'quotation_sample_information');
    const jenis = line ? dynamicRelation(line as DynamicEntityRow, 'jenis_pengukuran') : null;
    const nama = String(jenis?.nama_jenis ?? '').trim();
    const kode = String(jenis?.kode_jenis ?? '').trim();
    const label = nama || kode;
    if (label) labels.add(label);
  });
  return [...labels].sort((a, b) => a.localeCompare(b, 'id')).join(', ');
}

function enrichPersistedTeamRows(
  teamRows: DynamicEntityRow[],
  scheduleDocumentId: string,
  scheduleMeta?: ScheduleCalendarMeta,
): DynamicEntityRow[] {
  const samplingSchedule = buildSyntheticSamplingSchedule(scheduleDocumentId, scheduleMeta);
  return teamRows.map((row) => ({
    ...row,
    sampling_schedule: samplingSchedule,
  })) as DynamicEntityRow[];
}

function buildDraftTeamRowsFromState(
  teamDrafts: ScheduleTeamMemberDraft[],
  slotsByUserId: Map<string, string[]>,
  scheduleDocumentId: string,
  scheduleMeta?: ScheduleCalendarMeta,
  teamNamesByDate?: Record<string, string> | null,
): DynamicEntityRow[] {
  const samplingSchedule = buildSyntheticSamplingSchedule(scheduleDocumentId, scheduleMeta);
  const nameByDate = teamNamesByDate ?? {};
  const rows: DynamicEntityRow[] = [];
  for (const draft of teamDrafts) {
    const memberKey = draftMemberKey(draft);
    if (!memberKey) continue;
    const slots = slotsByUserId.get(memberKey) ?? [];
    if (!slots.length) continue;
    for (const rawSlot of slots) {
      const parsed = parseSurveyTeamScheduledSlots([rawSlot])[0];
      if (!parsed) continue;
      const resolvedTeamName = String(nameByDate[parsed.date] ?? '').trim() || null;
      if (draft.user) {
        const numericId =
          draft.user.numericId != null && Number.isFinite(Number(draft.user.numericId))
            ? Number(draft.user.numericId)
            : Number.isFinite(Number(draft.user.id))
              ? Number(draft.user.id)
              : null;
        rows.push({
          documentId: `draft-${memberKey}-${parsed.date}`,
          user_idx: {
            ...(numericId != null ? { id: numericId } : {}),
            documentId: draft.user.documentId,
            username: draft.user.username,
            email: draft.user.email,
          },
          scheduled_date: [rawSlot],
          sampling_schedule: samplingSchedule,
          is_lead: draft.is_lead,
          ...(resolvedTeamName ? { team_name: resolvedTeamName } : {}),
        } as unknown as DynamicEntityRow);
        continue;
      }
      if (draft.freelanceUser?.documentId) {
        rows.push({
          documentId: `draft-${memberKey}-${parsed.date}`,
          freelance_user: {
            documentId: draft.freelanceUser.documentId,
            name: draft.freelanceUser.name,
            phone_number: draft.freelanceUser.phone_number,
            active_status: draft.freelanceUser.active_status,
          },
          scheduled_date: [rawSlot],
          sampling_schedule: samplingSchedule,
          is_lead: draft.is_lead,
          ...(resolvedTeamName ? { team_name: resolvedTeamName } : {}),
        } as unknown as DynamicEntityRow);
      }
    }
  }
  return rows;
}

function enrichSummaryForCurrentSchedule(
  summary: QuotationScheduleDaySummary,
  scheduleDocumentId: string,
  scheduleStatus?: string | null,
  samplingReceivedAt?: string | null,
  quotationNumber?: string | null,
  customerName?: string | null,
  customerAddress?: string | null,
  scheduleNotes?: string | null,
  currentScheduleGroupLabel?: string | null,
  scheduleTransportasiNote?: string | null,
  teamNameForDate?: string | null,
  workOrderDocumentId?: string | null,
  workOrderJobNumber?: string | null,
): QuotationScheduleDaySummary {
  if (summary.scheduleDocumentId !== scheduleDocumentId) return summary;
  const syncedStatus = scheduleStatus?.trim();
  return {
    ...summary,
    scheduleStatus: syncedStatus || summary.scheduleStatus,
    quotationNumber: quotationNumber?.trim() || summary.quotationNumber,
    customerName: customerName?.trim() || summary.customerName,
    address: customerAddress?.trim() || summary.address,
    notes: scheduleNotes?.trim() || summary.notes,
    transportasiNote: scheduleTransportasiNote?.trim() || summary.transportasiNote,
    samplingReceivedAt: samplingReceivedAt?.trim() || summary.samplingReceivedAt,
    workOrderDocumentId:
      String(workOrderDocumentId ?? '').trim() || summary.workOrderDocumentId,
    workOrderJobNumber:
      String(workOrderJobNumber ?? '').trim() || summary.workOrderJobNumber,
    beritaAcaraAllIssued: summary.beritaAcaraAllIssued,
    groupLabel: currentScheduleGroupLabel ?? summary.groupLabel,
    teamName: teamNameForDate?.trim() || summary.teamName,
  };
}

function buildDateOnlySlots(startIso?: string | null, endIso?: string | null): string[] {
  const start = String(startIso ?? '').trim().slice(0, 10);
  const end = String(endIso ?? '').trim().slice(0, 10);
  if (!start) return [];
  const finish = end || start;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(finish)) return [];
  if (finish < start) return [start];
  return isoDatesInclusive(start, finish);
}

function uniqueCalendarDates(dates: Array<string | null | undefined>): string[] {
  return [
    ...new Set(
      dates
        .map((date) => String(date ?? '').trim().slice(0, 10))
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)),
    ),
  ].sort();
}

/**
 * Tanggal yang boleh nyala di modal kontrak/konsultan.
 * Start–end yang mengisi semua hari di antaranya bukan daftar tanggal sampling.
 */
function contractSlotModalDates(
  entryDates: string[],
  dateOnlyDates: string[],
  agreedDates: string[],
): string[] {
  const entries = uniqueCalendarDates(entryDates);
  const dateOnly = uniqueCalendarDates(dateOnlyDates);
  const agreed = uniqueCalendarDates(agreedDates);
  if (entries.length > 0 && agreed.length > 1) {
    const span = isoDatesInclusive(agreed[0]!, agreed[agreed.length - 1]!);
    const agreedFillsSpan =
      span.length === agreed.length && span.every((date, index) => date === agreed[index]);
    if (agreedFillsSpan && entries.length < agreed.length) return entries;
  }
  if (agreed.length > 0) return uniqueCalendarDates([...entries, ...dateOnly, ...agreed]);
  return uniqueCalendarDates([...entries, ...dateOnly]);
}

function buildDateOnlySlotsFromAgreed(
  customerAgreedDates?: string[] | null,
  startIso?: string | null,
  endIso?: string | null,
): string[] {
  const fromArray = (customerAgreedDates ?? [])
    .map((date) => String(date ?? '').trim().slice(0, 10))
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date));
  if (fromArray.length > 0) {
    return [...new Set(fromArray)].sort();
  }
  return buildDateOnlySlots(startIso, endIso);
}

function buildSlotAssignEntriesFromTeamState(
  teamDrafts: ScheduleTeamMemberDraft[],
  slotsByUserId: Map<string, string[]>,
): SlotAssignConfirmPayload[] {
  const entries: SlotAssignConfirmPayload[] = [];
  for (const draft of teamDrafts) {
    const memberKey = draftMemberKey(draft);
    if (!memberKey) continue;
    const daySlots = parseSurveyTeamScheduledSlots(slotsByUserId.get(memberKey) ?? []).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    daySlots.forEach((slot, index) => {
      const time = /^\d{2}:\d{2}$/.test(slot.timeLabel) ? slot.timeLabel : '08:00';
      entries.push({
        userId: memberKey,
        userName: draftMemberLabel(draft),
        startDate: slot.date,
        finishDate: slot.date,
        dates: [slot.date],
        time,
        isLead: draft.is_lead && index === 0,
      });
    });
  }
  return entries;
}

function datesFromSlotAssignPayload(payload: SlotAssignConfirmPayload): string[] {
  const fromDates = (payload.dates ?? [])
    .map((date) => String(date ?? '').trim().slice(0, 10))
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date));
  if (fromDates.length > 0) return [...new Set(fromDates)].sort();
  return isoDatesInclusive(payload.startDate, payload.finishDate);
}

function resolveManagedUserForSlotAssign(
  userId: string,
  users: StrapiManagedUserRow[],
  existingDrafts: ScheduleTeamMemberDraft[],
): StrapiManagedUserRow | null {
  const fromDraft = existingDrafts.find((draft) => draftMemberKey(draft) === userId)?.user;
  if (fromDraft) return fromDraft;
  return (
    users.find((row) => row.id === userId) ??
    users.find((row) => row.documentId === userId) ??
    users.find((row) => row.numericId != null && String(row.numericId) === userId) ??
    null
  );
}

function applyReplaceSlotAssignPayloads(
  payloads: SlotAssignConfirmPayload[],
  users: StrapiManagedUserRow[],
  freelanceUsers: StrapiFreelanceUserRow[],
  existingDrafts: ScheduleTeamMemberDraft[] = [],
): { drafts: ScheduleTeamMemberDraft[]; slots: Map<string, string[]> } {
  if (payloads.length === 0) {
    return { drafts: [], slots: new Map() };
  }

  const slots = new Map<string, string[]>();
  const leadByUserId = new Map<string, boolean>();

  for (const payload of payloads) {
    leadByUserId.set(
      payload.userId,
      (leadByUserId.get(payload.userId) ?? false) || payload.isLead,
    );
    const dates = datesFromSlotAssignPayload(payload);
    const newSlots = dates.map((date) => buildSurveyTeamSlotIso(date, payload.time));
    const existing = slots.get(payload.userId) ?? [];
    slots.set(payload.userId, [...new Set([...existing, ...newSlots])].sort());
  }

  const drafts: ScheduleTeamMemberDraft[] = [];
  let index = 0;
  for (const userId of slots.keys()) {
    if (isFreelanceMemberKey(userId)) {
      const freelanceDocId = freelanceDocumentIdFromMemberKey(userId);
      const freelanceUser =
        freelanceUsers.find((row) => row.documentId === freelanceDocId) ??
        existingDrafts.find((draft) => draftMemberKey(draft) === userId)?.freelanceUser ??
        null;
      if (!freelanceUser) continue;
      drafts.push({
        draftKey: `edited-${userId}-${index}`,
        user: null,
        freelanceUser,
        is_lead: leadByUserId.get(userId) ?? false,
      });
      index += 1;
      continue;
    }
    const user = resolveManagedUserForSlotAssign(userId, users, existingDrafts);
    if (!user) continue;
    drafts.push({
      draftKey: `edited-${userId}-${index}`,
      user,
      freelanceUser: null,
      is_lead: leadByUserId.get(userId) ?? false,
    });
    index += 1;
  }

  if (drafts.length > 0 && !drafts.some((draft) => draft.is_lead)) {
    drafts[0] = { ...drafts[0], is_lead: true };
  } else if (drafts.some((draft) => draft.is_lead)) {
    let leadAssigned = false;
    for (let i = 0; i < drafts.length; i += 1) {
      const isLead = Boolean(drafts[i].is_lead) && !leadAssigned;
      drafts[i] = { ...drafts[i], is_lead: isLead };
      if (isLead) leadAssigned = true;
    }
  }

  return { drafts, slots };
}

function applyAppendSlotAssignPayloads(
  payloads: SlotAssignConfirmPayload[],
  prevDrafts: ScheduleTeamMemberDraft[],
  prevSlots: Map<string, string[]>,
  users: StrapiManagedUserRow[],
  freelanceUsers: StrapiFreelanceUserRow[],
  draftCounter: number,
): { drafts: ScheduleTeamMemberDraft[]; slots: Map<string, string[]> } {
  let nextDrafts = [...prevDrafts];
  for (const payload of payloads) {
    const existingIdx = nextDrafts.findIndex((d) => draftMemberKey(d) === payload.userId);
    if (existingIdx >= 0) {
      if (payload.isLead) {
        nextDrafts = nextDrafts.map((d) => ({
          ...d,
          is_lead: draftMemberKey(d) === payload.userId,
        }));
      }
      continue;
    }

    let nextDraft: ScheduleTeamMemberDraft | null = null;
    if (isFreelanceMemberKey(payload.userId)) {
      const freelanceDocId = freelanceDocumentIdFromMemberKey(payload.userId);
      const freelanceUser =
        freelanceUsers.find((row) => row.documentId === freelanceDocId) ?? null;
      if (!freelanceUser) continue;
      nextDraft = {
        draftKey: `assigned-${draftCounter + nextDrafts.length}`,
        user: null,
        freelanceUser,
        is_lead: false,
      };
    } else {
      const matchedUser = users.find((u) => u.id === payload.userId);
      if (!matchedUser) continue;
      nextDraft = {
        draftKey: `assigned-${draftCounter + nextDrafts.length}`,
        user: matchedUser,
        freelanceUser: null,
        is_lead: false,
      };
    }

    const hasLead = nextDrafts.some((d) => d.is_lead && draftMemberKey(d));
    const makeLead = payload.isLead || !hasLead;
    nextDrafts = [
      ...nextDrafts.map((d) => (makeLead ? { ...d, is_lead: false } : d)),
      { ...nextDraft, is_lead: makeLead },
    ];
  }

  const nextSlots = new Map(prevSlots);
  for (const payload of payloads) {
    const dates = datesFromSlotAssignPayload(payload);
    const newSlots = dates.map((date) => buildSurveyTeamSlotIso(date, payload.time));
    const existing = nextSlots.get(payload.userId) ?? [];
    const parsed = parseSurveyTeamScheduledSlots(existing);
    const keep = parsed.filter((s) => !dates.includes(s.date)).map((s) => s.raw);
    nextSlots.set(payload.userId, [...new Set([...keep, ...newSlots])].sort());
  }

  return { drafts: nextDrafts, slots: nextSlots };
}

/** Tab gabungan: tanggal customer + kalender tim survey. */
export function SamplingSchedulePlanningTab(props: Props) {
  return <SamplingSchedulePlanningTabInner {...props} />;
}

/** @deprecated alias */
export const SamplingScheduleTeamTab = SamplingSchedulePlanningTab;

function SamplingSchedulePlanningTabInner({
  jwt,
  scheduleDocumentId,
  scheduleNumber,
  scheduleStatus,
  adminWorkorderCompletedAt,
  adminSamplingCompletedAt,
  samplingReceivedAt,
  quotationDocumentId,
  quotationNumber,
  quotationUsesGroups = false,
  quotationGroupMeta,
  scheduleQuotationGroup,
  recurringEventIndex = null,
  recurringEventDocumentId = null,
  customerName,
  customerAddress,
  scheduleNotes,
  scheduleTransportasiNote,
  workOrderDocumentId = null,
  workOrderJobNumber = null,
  anchorDate,
  anchorEndDate,
  customerAgreedDates = null,
  locked,
  editMode = 'readonly',
  teams,
  allSchedulesForCalendar = [],
  users,
  saving,
  teamSaveTick = 0,
  onSave,
  onRemoveScheduleDate,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const baseDate = anchorDate?.trim().slice(0, 10) || '';
  const [monthAnchor, setMonthAnchor] = useState(() =>
    baseDate
      ? { year: Number(baseDate.slice(0, 4)), month: Number(baseDate.slice(5, 7)) - 1 }
      : currentMonthAnchor(),
  );
  const [teamDrafts, setTeamDrafts] = useState<ScheduleTeamMemberDraft[]>([]);
  const [slotsByUserId, setSlotsByUserId] = useState<Map<string, string[]>>(new Map());
  const [teamNamesByDateDraft, setTeamNamesByDateDraft] = useState<Record<string, string>>({});
  const [dateOnlySlots, setDateOnlySlots] = useState<string[]>([]);
  const [scheduleNotesDraft, setScheduleNotesDraft] = useState('');
  const [scheduleTransportasiNoteDraft, setScheduleTransportasiNoteDraft] = useState('');
  const [pendingPlanDocumentIds, setPendingPlanDocumentIds] = useState<string[]>([]);
  const [pendingQuotationGroupDocumentId, setPendingQuotationGroupDocumentId] = useState('');
  const [draftCounter, setDraftCounter] = useState(0);
  const [localFreelanceExtras, setLocalFreelanceExtras] = useState<StrapiFreelanceUserRow[]>([]);
  const [scheduleDraftReady, setScheduleDraftReady] = useState(false);
  /** Isi `customer_agreed_dates` yang terakhir dipakai mengisi kartu kosong. */
  const syncedServerDateOnlyKeyRef = useRef<string | null>(null);
  const [detailDialog, setDetailDialog] = useState<{
    date: string;
    summary: QuotationScheduleDaySummary;
  } | null>(null);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [assignDialog, setAssignDialog] = useState<{
    anchorDate: string;
    weekAnchorDate: string;
    mode: 'add' | 'edit';
  } | null>(null);
  const [assignSaveError, setAssignSaveError] = useState<string | null>(null);
  /** Tanggal yang baru dihapus, disembunyikan sampai server tidak lagi mengirimnya. */
  const [hiddenScheduleDateKeys, setHiddenScheduleDateKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const canEditFull = editMode === 'full';
  const canEditDateOnly = editMode === 'date_only';
  /** Date-only: boleh buka modal walau tanggal sudah ada (ubah lewat + / kartu). */
  const canAddSlot = canEditFull || canEditDateOnly;
  const canPersistSchedule = canEditFull || (canEditDateOnly && dateOnlySlots.length > 0);

  useEffect(() => {
    const editDate = String(
      (location.state as { editScheduleDate?: string } | null)?.editScheduleDate ?? '',
    ).trim();
    if (!editDate || !/^\d{4}-\d{2}-\d{2}$/.test(editDate)) return;
    if (!scheduleDraftReady) return;
    if (locked || !(canEditFull || canEditDateOnly)) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    setAssignDialog({
      anchorDate: editDate,
      weekAnchorDate: editDate,
      mode: 'edit',
    });
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.state,
    location.pathname,
    scheduleDraftReady,
    locked,
    canEditFull,
    canEditDateOnly,
    navigate,
  ]);

  const assignedMemberCount = useMemo(
    () =>
      teamDrafts.filter((draft) => {
        const memberKey = draftMemberKey(draft);
        return Boolean(memberKey && (slotsByUserId.get(memberKey)?.length ?? 0) > 0);
      }).length,
    [teamDrafts, slotsByUserId],
  );
  const canSaveFullTeam = !canEditFull || assignedMemberCount >= 2;
  const canAddFreelanceUser = isAdminSampling(user);
  const canEditTransportasiNote = canEditScheduleTransportasiNote(user);

  const scheduleQ = useSurveyTeamsScheduleQuery(jwt, { userNumericId: null });
  const issuedQ = useIssuedBeritaAcaraWorkOrderIds(jwt);
  const surveyOfficersQ = useUsersQuery(jwt, {
    emailContains: '',
    status: 'active',
    userTypeCode: 'petugas_sampling',
  });
  const surveyOfficers = surveyOfficersQ.data?.rows ?? [];
  const freelanceUsersQ = useQuery({
    queryKey: ['strapi', 'freelanceUsers', jwt, 'active', 'petugas_sampling'],
    queryFn: () =>
      strapiListFreelanceUsers(jwt!, {
        status: 'active',
        userTypeCode: 'petugas_sampling',
      }),
    enabled: Boolean(jwt) && canEditFull,
    staleTime: 30_000,
  });
  const freelanceUsers = useMemo(() => {
    const byId = new Map<string, StrapiFreelanceUserRow>();
    for (const row of [...(freelanceUsersQ.data ?? []), ...localFreelanceExtras]) {
      byId.set(row.documentId, row);
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'id'));
  }, [freelanceUsersQ.data, localFreelanceExtras]);
  const hasCurrentQuotationSchedule = useMemo(
    () =>
      teamDrafts.some((draft) => {
        const memberKey = draftMemberKey(draft);
        return Boolean(memberKey && (slotsByUserId.get(memberKey)?.length ?? 0) > 0);
      }),
    [teamDrafts, slotsByUserId],
  );
  const hasDateOnlySchedule = dateOnlySlots.length > 0;
  const defaultSurveyRange = useMemo(
    () =>
      defaultSurveyRangeFromSchedule({
        dateOnlySlots,
        startDate: anchorDate,
        endDate: anchorEndDate,
      }),
    [dateOnlySlots, anchorDate, anchorEndDate],
  );
  const ensureGroupedScheduleQ = useQuery({
    queryKey: ['strapi', 'ensureGroupedSchedulePlans', jwt, quotationDocumentId],
    queryFn: () => strapiEnsureSamplingScheduleForQuotation(jwt!, quotationDocumentId!),
    enabled: Boolean(jwt && quotationDocumentId && quotationUsesGroups),
  });
  const plansQ = useQuery({
    queryKey: ['strapi', 'samplingPlansForQuotation', jwt, quotationDocumentId],
    queryFn: () => strapiListSamplingPlansForQuotation(jwt!, quotationDocumentId!),
    enabled:
      Boolean(jwt && quotationDocumentId) &&
      (!quotationUsesGroups || ensureGroupedScheduleQ.isSuccess),
  });
  const sampleGroupOptions = useMemo(() => {
    const options = buildUnscheduledSampleGroupOptions(
      plansQ.data ?? [],
      scheduleDocumentId,
      quotationGroupMeta ?? { is_contract: false, is_consultant_quote: false },
      pendingPlanDocumentIds,
      false,
    );
    if (!quotationGroupMeta?.is_contract) return options;
    return contractSampleGroupOptionForSchedule(
      options,
      scheduleDocumentId,
      recurringEventDocumentId,
    );
  }, [
    plansQ.data,
    scheduleDocumentId,
    quotationGroupMeta,
    pendingPlanDocumentIds,
    recurringEventDocumentId,
  ]);
  const sampleGroupsLoading =
    ensureGroupedScheduleQ.isPending || plansQ.isPending || plansQ.isFetching;
  const quotationLabel = quotationNumber?.trim() || 'Quotation ini';
  const assignDialogDate = assignDialog?.anchorDate ?? null;
  const assignDialogWeekAnchorDate = assignDialog?.weekAnchorDate ?? null;

  const editInitialEntries = useMemo(
    () => buildSlotAssignEntriesFromTeamState(teamDrafts, slotsByUserId),
    [teamDrafts, slotsByUserId],
  );
  const slotModalDates = useMemo(() => {
    const entryDates = editInitialEntries.flatMap((entry) => entry.dates ?? [entry.startDate]);
    const groupedQuote = Boolean(
      quotationGroupMeta?.is_contract || quotationGroupMeta?.is_consultant_quote,
    );
    if (!groupedQuote) {
      return uniqueCalendarDates([
        ...(dateOnlySlots ?? []),
        ...(customerAgreedDates ?? []),
        ...entryDates,
      ]);
    }
    return contractSlotModalDates(entryDates, dateOnlySlots ?? [], customerAgreedDates ?? []);
  }, [
    editInitialEntries,
    dateOnlySlots,
    customerAgreedDates,
    quotationGroupMeta?.is_contract,
    quotationGroupMeta?.is_consultant_quote,
  ]);

  useEffect(() => {
    setScheduleNotesDraft(scheduleNotes?.trim() ?? '');
  }, [scheduleNotes]);

  useEffect(() => {
    setScheduleTransportasiNoteDraft(scheduleTransportasiNote?.trim() ?? '');
  }, [scheduleTransportasiNote]);

  useEffect(() => {
    const id = String(scheduleQuotationGroup?.documentId ?? '').trim();
    if (!id) return;
    setPendingQuotationGroupDocumentId((prev) => prev || id);
  }, [scheduleQuotationGroup?.documentId]);

  useEffect(() => {
    if (!baseDate) return;
    setMonthAnchor({
      year: Number(baseDate.slice(0, 4)),
      month: Number(baseDate.slice(5, 7)) - 1,
    });
  }, [baseDate]);

  useEffect(() => {
    if (teamSaveTick > 0) {
      setPendingPlanDocumentIds([]);
      setPendingQuotationGroupDocumentId(String(scheduleQuotationGroup?.documentId ?? '').trim());
    }
  }, [teamSaveTick, scheduleQuotationGroup?.documentId]);

  useEffect(() => {
    setTeamDrafts([]);
    setSlotsByUserId(new Map());
    setTeamNamesByDateDraft({});
    setDateOnlySlots([]);
    setPendingPlanDocumentIds([]);
    setPendingQuotationGroupDocumentId(String(scheduleQuotationGroup?.documentId ?? '').trim());
    setDraftCounter(0);
    setLocalFreelanceExtras([]);
    setScheduleDraftReady(false);
    syncedServerDateOnlyKeyRef.current = null;
  }, [scheduleDocumentId]);

  useEffect(() => {
    const serverDates = buildDateOnlySlotsFromAgreed(customerAgreedDates, anchorDate, anchorEndDate);
    const serverKey = serverDates.join('|');
    const serverDatesChanged = syncedServerDateOnlyKeyRef.current !== serverKey;
    syncedServerDateOnlyKeyRef.current = serverKey;

    if (!teams.length) {
      setTeamDrafts([]);
      setSlotsByUserId(new Map());
      setTeamNamesByDateDraft({});
      setDateOnlySlots(serverDates);
      setScheduleDraftReady(true);
      return;
    }

    const { drafts, slotsByUserId: slotMap } = collectTeamDraftStateFromRows(
      teams,
      scheduleDocumentId,
      users,
      freelanceUsersQ.data ?? [],
    );
    setTeamDrafts(drafts);
    setSlotsByUserId(slotMap);
    setTeamNamesByDateDraft(collectTeamNamesByDateFromRows(teams, scheduleDocumentId));
    // Tanggal kosong yang belum disimpan tetap dipertahankan.
    // Setelah refetch mengubah customer_agreed_dates, pakai daftar server supaya
    // tanggal yang sudah dipindah tidak tertinggal di kalender.
    setDateOnlySlots((prev) => {
      const occupiedDates = new Set<string>();
      for (const rawList of slotMap.values()) {
        for (const parsed of parseSurveyTeamScheduledSlots(rawList)) {
          occupiedDates.add(parsed.date);
        }
      }
      const base = serverDatesChanged || prev.length === 0 ? serverDates : prev;
      return base.filter((date) => !occupiedDates.has(date));
    });
    setScheduleDraftReady(true);
  }, [
    teams,
    users,
    freelanceUsersQ.data,
    scheduleDocumentId,
    anchorDate,
    anchorEndDate,
    customerAgreedDates,
  ]);

  const linkedPlansForSchedule = useMemo(() => {
    if (!quotationUsesGroups) return [];
    return (plansQ.data ?? []).filter((plan) => {
      const sched = dynamicRelation(plan, 'sampling_schedule');
      return String(sched?.documentId ?? '').trim() === scheduleDocumentId;
    });
  }, [plansQ.data, quotationUsesGroups, scheduleDocumentId]);

  const currentScheduleGroupDocumentId = useMemo(() => {
    const fromSchedule = String(scheduleQuotationGroup?.documentId ?? '').trim();
    if (fromSchedule) return fromSchedule;
    for (const plan of linkedPlansForSchedule) {
      const planGroup = dynamicRelation(plan, 'quotation_group');
      const fromPlan = String(planGroup?.documentId ?? '').trim();
      if (fromPlan) return fromPlan;
    }
    return '';
  }, [scheduleQuotationGroup, linkedPlansForSchedule]);

  const currentScheduleMeasurementTypesLabel = useMemo(() => {
    const allPlans = plansQ.data ?? [];
    if (allPlans.length === 0) return '';
    if (!quotationUsesGroups) {
      return measurementTypesLabelFromPlans(allPlans);
    }
    const pendingSet = new Set(pendingPlanDocumentIds.map((id) => id.trim()).filter(Boolean));
    const sourcePlans =
      pendingSet.size > 0
        ? allPlans.filter((plan) => pendingSet.has(String(plan.documentId ?? '').trim()))
        : linkedPlansForSchedule;
    return measurementTypesLabelFromPlans(sourcePlans);
  }, [plansQ.data, quotationUsesGroups, pendingPlanDocumentIds, linkedPlansForSchedule]);

  const currentScheduleGroupLabel = useMemo(() => {
    if (!quotationUsesGroups || !quotationGroupMeta) return null;
    const groupIndexByDocId = quotationGroupIndexFromPlans(plansQ.data ?? []);
    const scheduleSnapshot = buildSyntheticSamplingSchedule(scheduleDocumentId, {
      scheduleQuotationGroup,
      linkedSamplingPlans: linkedPlansForSchedule,
    }) as DynamicEntityRow;
    const quotation = {
      documentId: quotationDocumentId ?? '',
      ...quotationGroupMeta,
    };
    const groupDocId = String(scheduleQuotationGroup?.documentId ?? '').trim();
    const groupIndex = groupDocId ? (groupIndexByDocId.get(groupDocId) ?? 0) : 0;
    return buildGroupLabelForSchedule(scheduleSnapshot, quotation, groupIndex);
  }, [
    quotationUsesGroups,
    quotationGroupMeta,
    plansQ.data,
    scheduleDocumentId,
    scheduleQuotationGroup,
    linkedPlansForSchedule,
    quotationDocumentId,
  ]);

  const scheduleMeta = useMemo(
    () => ({
      scheduleNumber,
      scheduleStatus,
      quotationDocumentId,
      quotationNumber,
      customerName,
      customerAddress,
      scheduleNotes: scheduleNotesDraft,
      scheduleTransportasiNote: scheduleTransportasiNoteDraft,
      measurementTypesLabel: currentScheduleMeasurementTypesLabel,
      workOrderDocumentId,
      workOrderJobNumber,
      quotationGroupMeta,
      scheduleQuotationGroup,
      recurringEventIndex,
      linkedSamplingPlans: linkedPlansForSchedule,
    }),
    [
      scheduleNumber,
      scheduleStatus,
      quotationDocumentId,
      quotationNumber,
      customerName,
      customerAddress,
      scheduleNotesDraft,
      scheduleTransportasiNoteDraft,
      currentScheduleMeasurementTypesLabel,
      workOrderDocumentId,
      workOrderJobNumber,
      quotationGroupMeta,
      scheduleQuotationGroup,
      recurringEventIndex,
      linkedPlansForSchedule,
    ],
  );

  const { calendarTeams, dateOnlySummariesByDate: mergedExtraSummariesByDate } = useMemo(() => {
    const draftRows = buildDraftTeamRowsFromState(
      teamDrafts,
      slotsByUserId,
      scheduleDocumentId,
      scheduleMeta,
      teamNamesByDateDraft,
    );
    // Fallback tim work order milik jadwal lain tidak boleh menimpa kartu jadwal ini.
    // Kalau ikut, jadwal yang petugasnya masih kosong hilang dari kalender.
    const ownTeams = teams.filter((row) => {
      const teamScheduleId = scheduleDocIdFromTeamRow(row);
      return !teamScheduleId || teamScheduleId === scheduleDocumentId;
    });
    const replacementTeams =
      draftRows.length > 0
        ? draftRows
        : ownTeams.length > 0
          ? enrichPersistedTeamRows(ownTeams, scheduleDocumentId, scheduleMeta)
          : [];

    const localDateOnlySummary: QuotationScheduleDaySummary | null =
      dateOnlySlots.length > 0
        ? {
            quotationDocumentId: quotationDocumentId?.trim() ?? '',
            quotationNumber: quotationNumber?.trim() || 'Quotation',
            scheduleDocumentId,
            scheduleNumber: scheduleNumber?.trim() || scheduleDocumentId,
            scheduleStatus: scheduleStatus?.trim() || 'scheduling_in_progress',
            customerName: customerName?.trim() || '—',
            address: customerAddress?.trim() || '',
            notes: scheduleNotesDraft,
            transportasiNote: scheduleTransportasiNoteDraft,
            measurementTypesLabel: currentScheduleMeasurementTypesLabel,
            samplingReceivedAt: samplingReceivedAt?.trim() || '',
            workOrderDocumentId:
              String(workOrderDocumentId ?? '').trim() ||
              allSchedulesForCalendar.find((row) => row.documentId === scheduleDocumentId)?.work_order
                ?.documentId?.trim() ||
              '',
            workOrderJobNumber:
              String(workOrderJobNumber ?? '').trim() ||
              allSchedulesForCalendar.find((row) => row.documentId === scheduleDocumentId)?.work_order
                ?.job_number?.trim() ||
              '',
            beritaAcaraAllIssued: false,
            teamLeadName: '—',
            teamCount: 0,
            members: [],
            teamName: null,
            groupLabel: currentScheduleGroupLabel,
            isContract: Boolean(quotationGroupMeta?.is_contract),
            recurringEventIndex,
          }
        : null;

    return buildDetailPageScheduleCalendar(scheduleQ.data ?? [], allSchedulesForCalendar, {
      scheduleDocumentId,
      hasTeamAssignments: hasCurrentQuotationSchedule,
      replacementTeams,
      localDateOnlySummary,
      localDateOnlyDates: dateOnlySlots,
      applyEmptyReplacement: scheduleDraftReady,
    });
  }, [
    scheduleQ.data,
    allSchedulesForCalendar,
    scheduleDocumentId,
    teamDrafts,
    slotsByUserId,
    teams,
    scheduleMeta,
    hasCurrentQuotationSchedule,
    dateOnlySlots,
    quotationDocumentId,
    quotationNumber,
    scheduleNumber,
    scheduleStatus,
    customerName,
    customerAddress,
    scheduleNotesDraft,
    scheduleTransportasiNoteDraft,
    currentScheduleMeasurementTypesLabel,
    samplingReceivedAt,
    currentScheduleGroupLabel,
    scheduleDraftReady,
    teamNamesByDateDraft,
    workOrderDocumentId,
    workOrderJobNumber,
  ]);

  const visibleCalendarTeams = useMemo(
    () => omitHiddenScheduleDatesFromTeams(calendarTeams, hiddenScheduleDateKeys),
    [calendarTeams, hiddenScheduleDateKeys],
  );
  const visibleExtraSummariesByDate = useMemo(
    () => omitHiddenScheduleDatesFromSummaries(mergedExtraSummariesByDate, hiddenScheduleDateKeys),
    [mergedExtraSummariesByDate, hiddenScheduleDateKeys],
  );

  useEffect(() => {
    if (hiddenScheduleDateKeys.size === 0) return;
    const serverTeams = scheduleQ.data ?? [];
    setHiddenScheduleDateKeys((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      let changed = false;
      for (const key of prev) {
        const sep = key.indexOf('|');
        if (sep <= 0) continue;
        const scheduleId = key.slice(0, sep);
        const date = key.slice(sep + 1);
        const stillThere =
          scheduleDateStillOnServer(scheduleId, date, serverTeams, allSchedulesForCalendar) ||
          scheduleDateStillOnServer(scheduleId, date, teams, allSchedulesForCalendar);
        if (!stillThere) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [hiddenScheduleDateKeys, scheduleQ.data, teams, allSchedulesForCalendar]);

  const userCandidates = useMemo(() => {
    const officers = surveyOfficers
      .filter((u) => u.numericId != null)
      .map((u) => ({
        id: u.id,
        documentId: u.documentId ?? undefined,
        numericId: u.numericId,
        name: userDisplayName(u),
        isLead: teamDrafts.some((d) => d.user?.id === u.id && d.is_lead),
      }));
    const freelancers = freelanceUsers.map((row) => {
      const id = freelanceMemberKey(row.documentId);
      return {
        id,
        documentId: row.documentId,
        numericId: null as number | null,
        name: `${row.name.trim() || 'Freelance'} (freelance)`,
        isLead: teamDrafts.some(
          (d) => d.freelanceUser?.documentId === row.documentId && d.is_lead,
        ),
      };
    });
    return [...officers, ...freelancers].sort((a, b) => a.name.localeCompare(b.name, 'id'));
  }, [surveyOfficers, freelanceUsers, teamDrafts]);

  const assignAvailability = useMemo(() => {
    if (!assignDialogDate) return [];
    return surveyTeamAvailabilityForUsers(
      assignDialogDate,
      userCandidates,
      calendarTeams,
      scheduleDocumentId,
    );
  }, [assignDialogDate, userCandidates, calendarTeams, scheduleDocumentId]);

  const weekAvailabilityByUserId = useMemo(() => {
    if (!assignDialogWeekAnchorDate) return new Map();
    const weekDates = weekDatesForwardFrom(assignDialogWeekAnchorDate, 7);
    const map = new Map<string, Map<string, ReturnType<typeof surveyTeamAvailabilityForUsers>[number]>>();
    for (const user of userCandidates) {
      const dayMap = new Map<string, ReturnType<typeof surveyTeamAvailabilityForUsers>[number]>();
      for (const date of weekDates) {
        const [row] = surveyTeamAvailabilityForUsers(date, [user], calendarTeams, scheduleDocumentId);
        if (row) dayMap.set(date, row);
      }
      map.set(user.id, dayMap);
    }
    return map;
  }, [assignDialogWeekAnchorDate, userCandidates, calendarTeams, scheduleDocumentId]);

  function handleSlotAssignConfirm(
    payloads: SlotAssignConfirmPayload[],
    scheduleMetaNotes: SlotAssignScheduleNotes,
    dateOnlySelection?: {
      startDate: string;
      finishDate: string;
      dates?: string[];
      planDocumentIds?: string[];
      quotationGroupDocumentId?: string;
      quotationRecurringEventDocumentId?: string;
    },
    workOrderCreate?: SlotAssignWorkOrderPayload,
    replace = false,
  ) {
    setAssignSaveError(null);
    const planIds = [
      ...new Set([
        ...collectPlanDocumentIdsFromPayloads(payloads),
        ...(dateOnlySelection?.planDocumentIds ?? []),
      ]),
    ];
    const incomingGroupId = (
      dateOnlySelection?.quotationGroupDocumentId
      ?? payloads.find((payload) => payload.quotationGroupDocumentId?.trim())?.quotationGroupDocumentId
      ?? ''
    ).trim();
    const currentBoundGroup = currentScheduleGroupDocumentId;
    const persistToOtherGroup = Boolean(
      incomingGroupId &&
        ((currentBoundGroup && incomingGroupId !== currentBoundGroup) ||
          (pendingQuotationGroupDocumentId && incomingGroupId !== pendingQuotationGroupDocumentId)),
    );
    const incomingEventId = (
      dateOnlySelection?.quotationRecurringEventDocumentId
      ?? payloads.find((payload) => payload.quotationRecurringEventDocumentId?.trim())
        ?.quotationRecurringEventDocumentId
      ?? ''
    ).trim();
    const groupIdToSave = incomingGroupId || pendingQuotationGroupDocumentId || currentBoundGroup || undefined;

    const resolveDateOnlySlots = (selection: {
      startDate: string;
      finishDate: string;
      dates?: string[];
    }): string[] => {
      const fromDates = (selection.dates ?? [])
        .map((date) => String(date ?? '').trim().slice(0, 10))
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date));
      if (fromDates.length > 0) return [...new Set(fromDates)].sort();
      return isoDatesInclusive(selection.startDate, selection.finishDate);
    };

    const persist = (
      members: ScheduleTeamMemberDraft[],
      slots: Map<string, string[]>,
      dateOnly: string[] | undefined,
      allowEmptyTeam: boolean,
      groupId: string | undefined,
      teamNamesByDate?: Record<string, string> | null,
      keepCurrentSchedule = true,
      appendRoster?: ScheduleAppendRoster | null,
    ) => {
      onSave(
        members,
        slots,
        scheduleMetaNotes.notes,
        planIds,
        dateOnly,
        scheduleMetaNotes.transportasiNote,
        {
          stayOnPage: true,
          allowEmptyTeam,
          quotationGroupDocumentId: groupId,
          quotationRecurringEventDocumentId: incomingEventId || undefined,
          // Simpan ke jadwal yang sedang dibuka. Group lain tetap lewat ensure.
          targetScheduleDocumentId: keepCurrentSchedule ? scheduleDocumentId : undefined,
          workOrderCreate,
          teamNamesByDate: teamNamesByDate ?? teamNamesByDateDraft,
          appendRoster: appendRoster ?? undefined,
        },
      );
    };

    const userLookup = (() => {
      const byKey = new Map<string, StrapiManagedUserRow>();
      for (const row of [...users, ...surveyOfficers]) {
        byKey.set(row.id, row);
        if (row.documentId) byKey.set(row.documentId, row);
        if (row.numericId != null) byKey.set(String(row.numericId), row);
      }
      return [...byKey.values()];
    })();

    if (persistToOtherGroup) {
      if (dateOnlySelection) {
        persist(
          [],
          new Map(),
          resolveDateOnlySlots(dateOnlySelection),
          true,
          incomingGroupId,
          undefined,
          false,
        );
        return;
      }
      const { drafts, slots } = applyReplaceSlotAssignPayloads(
        payloads,
        userLookup,
        freelanceUsers,
        teamDrafts,
      );
      persist(drafts, slots, undefined, false, incomingGroupId, undefined, false);
      return;
    }

    setScheduleNotesDraft(scheduleMetaNotes.notes);
    setScheduleTransportasiNoteDraft(scheduleMetaNotes.transportasiNote);
    const nextTeamNames =
      scheduleMetaNotes.teamNamesByDate != null
        ? { ...scheduleMetaNotes.teamNamesByDate }
        : teamNamesByDateDraft;
    if (scheduleMetaNotes.teamNamesByDate != null) {
      setTeamNamesByDateDraft(nextTeamNames);
    }
    if (incomingGroupId) {
      setPendingQuotationGroupDocumentId(incomingGroupId);
    }

    if (dateOnlySelection && payloads.length === 0) {
      const slots = resolveDateOnlySlots(dateOnlySelection);
      if (replace) {
        const occupiedDates = assignedDatesForSchedule(slotsByUserId, teams, scheduleDocumentId);
        const copiedRoster = canEditFull
          ? copiedRosterForEmptyScheduleDates({
              teamRows: teams,
              scheduleDocumentId,
              selectedDates: slots,
              occupiedDates,
              users: userLookup,
              freelanceUsers,
            })
          : null;
        if (copiedRoster) {
          const copiedDates = new Set(copiedRoster.dates);
          const nextSlots = new Map<string, string[]>(
            [...slotsByUserId.entries()].map(([key, value]) => [key, [...value]]),
          );
          for (const date of copiedRoster.dates) {
            for (const member of copiedRoster.members) {
              const time = /^\d{2}:\d{2}$/.test(member.time) ? member.time : '08:00';
              let rawSlot = date;
              try {
                rawSlot = buildSurveyTeamSlotIso(date, time);
              } catch {
                rawSlot = date;
              }
              const existing = nextSlots.get(member.memberKey) ?? [];
              if (!parseSurveyTeamScheduledSlots(existing).some((slot) => slot.date === date)) {
                nextSlots.set(member.memberKey, [...existing, rawSlot]);
              }
            }
          }
          const draftsByKey = new Map<string, ScheduleTeamMemberDraft>();
          for (const draft of teamDrafts) {
            const key = draftMemberKey(draft);
            if (key) draftsByKey.set(key, draft);
          }
          copiedRoster.members.forEach((member, index) => {
            const existing = draftsByKey.get(member.memberKey);
            if (existing) {
              if (member.isLead && !existing.is_lead) {
                draftsByKey.set(member.memberKey, { ...existing, is_lead: true });
              }
              return;
            }
            draftsByKey.set(member.memberKey, {
              draftKey: `copy-roster-${member.memberKey}-${draftCounter + index}`,
              user: member.user,
              freelanceUser: member.freelanceUser,
              is_lead: member.isLead,
            });
          });
          const rosterTeamNames = { ...nextTeamNames };
          const copiedTeamName = String(copiedRoster.teamName ?? '').trim();
          if (copiedTeamName) {
            for (const date of copiedRoster.dates) {
              if (!String(rosterTeamNames[date] ?? '').trim()) rosterTeamNames[date] = copiedTeamName;
            }
          }
          setDateOnlySlots(slots.filter((date) => !occupiedDates.has(date) && !copiedDates.has(date)));
          setTeamDrafts([...draftsByKey.values()]);
          setSlotsByUserId(nextSlots);
          setTeamNamesByDateDraft(rosterTeamNames);
          setDraftCounter((n) => n + copiedRoster.members.length);
          setPendingPlanDocumentIds(planIds);
          persist(
            [],
            new Map(),
            slots,
            true,
            groupIdToSave,
            rosterTeamNames,
            true,
            {
              dates: copiedRoster.dates,
              teamName: copiedRoster.teamName,
              members: copiedRoster.members.map((member) => ({
                user: member.user,
                freelanceUser: member.freelanceUser,
                isLead: member.isLead,
                time: member.time,
              })),
            },
          );
          return;
        }
        setDateOnlySlots(slots.filter((date) => !occupiedDates.has(date)));
        setPendingPlanDocumentIds(planIds);
        persist([], new Map(), slots, true, groupIdToSave, nextTeamNames);
        return;
      }
      setDateOnlySlots(slots);
      setTeamDrafts([]);
      setSlotsByUserId(new Map());
      setDraftCounter((n) => n + 1);
      setPendingPlanDocumentIds(planIds);
      persist([], new Map(), slots, true, groupIdToSave, nextTeamNames);
      return;
    }

    if (replace) {
      const { drafts, slots } = applyReplaceSlotAssignPayloads(
        payloads,
        userLookup,
        freelanceUsers,
        teamDrafts,
      );
      if (payloads.length > 0 && drafts.length === 0) {
        // Jangan jatuh ke date-only save: itu membuat kalender tampak "tidak berubah".
        setAssignSaveError(
          'Gagal memperbarui petugas di kalender. Pastikan petugas masih aktif lalu coba lagi.',
        );
        return;
      }
      setAssignSaveError(null);
      const assignedDates = new Set<string>();
      for (const rawList of slots.values()) {
        for (const parsed of parseSurveyTeamScheduledSlots(rawList)) {
          assignedDates.add(parsed.date);
        }
      }
      const scheduleDates = dateOnlySelection
        ? resolveDateOnlySlots(dateOnlySelection)
        : [...assignedDates].sort();
      const emptyDates = scheduleDates.filter((date) => !assignedDates.has(date));
      setDateOnlySlots(emptyDates);
      setTeamDrafts(drafts);
      setSlotsByUserId(slots);
      setDraftCounter((n) => n + Math.max(payloads.length, 1));
      setPendingPlanDocumentIds(planIds);
      const assignedCount = drafts.filter((draft) => {
        const key = draftMemberKey(draft);
        return Boolean(key && (slots.get(key)?.length ?? 0) > 0);
      }).length;
      // Modal edit harus langsung persist agar kalender & DB ikut berubah.
      // dateOnly: hanya tanggal kosong (tanpa petugas) agar customer_agreed tetap lengkap.
      persist(drafts, slots, scheduleDates, assignedCount < 2, groupIdToSave, nextTeamNames);
      return;
    }

    const { drafts, slots } = applyAppendSlotAssignPayloads(
      payloads,
      teamDrafts,
      slotsByUserId,
      userLookup,
      freelanceUsers,
      draftCounter,
    );
    setTeamDrafts(drafts);
    setSlotsByUserId(slots);
    setDraftCounter((n) => n + Math.max(payloads.length, 1));
    setDateOnlySlots([]);
    setPendingPlanDocumentIds((prev) => [...new Set([...prev, ...planIds])]);
    if (
      drafts.filter((draft) => {
        const key = draftMemberKey(draft);
        return Boolean(key && (slots.get(key)?.length ?? 0) > 0);
      }).length >= 2
    ) {
      persist(drafts, slots, undefined, false, groupIdToSave, nextTeamNames);
    }
  }

  function openDetailDialog(date: string, summary: QuotationScheduleDaySummary) {
    setDetailDialog({
      date,
      summary: enrichSummaryForCurrentSchedule(
        summary,
        scheduleDocumentId,
        scheduleStatus,
        samplingReceivedAt,
        quotationNumber,
        customerName,
        customerAddress,
        scheduleNotesDraft,
        currentScheduleGroupLabel,
        scheduleTransportasiNoteDraft,
        teamNamesByDateDraft[date] ?? summary.teamName,
        workOrderDocumentId,
        workOrderJobNumber,
      ),
    });
  }

  function handleSaveTeamMembers(
    date: string,
    summary: QuotationScheduleDaySummary,
    payload: {
      teamName: string;
      members: Array<{ userId: string; name: string; isLead: boolean }>;
    },
  ) {
    if (!scheduleCardIsEditable(summary)) return;
    setAssignSaveError(null);

    const targetScheduleId = summary.scheduleDocumentId.trim() || scheduleDocumentId;
    const isCurrent = targetScheduleId === scheduleDocumentId;

    const userLookup = (() => {
      const byKey = new Map<string, StrapiManagedUserRow>();
      for (const row of [...users, ...surveyOfficers]) {
        byKey.set(row.id, row);
        if (row.documentId) byKey.set(row.documentId, row);
        if (row.numericId != null) byKey.set(String(row.numericId), row);
      }
      return byKey;
    })();

    const time =
      summary.members
        .flatMap((member) => member.timeLabels)
        .map((label) => label.trim())
        .find((label) => /^\d{2}:\d{2}/.test(label))
        ?.slice(0, 5) || '08:00';
    const dateSlot = (() => {
      try {
        return buildSurveyTeamSlotIso(date, time);
      } catch {
        return date;
      }
    })();

    const drafts: ScheduleTeamMemberDraft[] = [];
    const slots = new Map<string, string[]>();
    let index = 0;
    for (const member of payload.members) {
      const userId = member.userId.trim();
      if (!userId) continue;
      if (isFreelanceMemberKey(userId)) {
        const freelanceDocId = freelanceDocumentIdFromMemberKey(userId);
        const freelanceUser =
          freelanceUsers.find((row) => row.documentId === freelanceDocId) ?? null;
        if (!freelanceUser) continue;
        drafts.push({
          draftKey: `team-edit-${userId}-${index}`,
          user: null,
          freelanceUser,
          is_lead: Boolean(member.isLead),
        });
        slots.set(userId, [dateSlot]);
        index += 1;
        continue;
      }
      const user = userLookup.get(userId) ?? null;
      if (!user) continue;
      drafts.push({
        draftKey: `team-edit-${userId}-${index}`,
        user,
        freelanceUser: null,
        is_lead: Boolean(member.isLead),
      });
      slots.set(String(user.id), [dateSlot]);
      index += 1;
    }

    if (drafts.length === 0) {
      setAssignSaveError('Gagal memperbarui petugas. Pastikan petugas masih aktif.');
      return;
    }
    if (!drafts.some((draft) => draft.is_lead)) {
      drafts[0] = { ...drafts[0]!, is_lead: true };
    }

    const nextTeamName = payload.teamName.trim();
    const nextTeamNamesByDate = {
      ...teamNamesByDateDraft,
      ...(nextTeamName ? { [date]: nextTeamName } : {}),
    };
    if (!nextTeamName) {
      delete nextTeamNamesByDate[date];
    }
    if (isCurrent) {
      const existingSlotsSource =
        slotsByUserId.size > 0
          ? slotsByUserId
          : collectTeamDraftStateFromRows(
              teams,
              targetScheduleId,
              users,
              freelanceUsers,
            ).slotsByUserId;
      const nextSlots = buildSlotsAdoptingTeamOnSingleDate({
        previousSlots: existingSlotsSource,
        fromDate: date,
        toDate: date,
        teamMembers: drafts.map((draft) => ({
          userId: draftMemberKey(draft) ?? '',
          timeLabels: [time],
        })),
      });
      const byKey = new Map<string, ScheduleTeamMemberDraft>();
      for (const draft of teamDrafts) {
        const key = draftMemberKey(draft);
        if (!key || (nextSlots.get(key)?.length ?? 0) === 0) continue;
        byKey.set(key, draft);
      }
      for (const draft of drafts) {
        const key = draftMemberKey(draft);
        if (!key || (nextSlots.get(key)?.length ?? 0) === 0) continue;
        byKey.set(key, draft);
      }
      setTeamDrafts([...byKey.values()]);
      setSlotsByUserId(nextSlots);
      setTeamNamesByDateDraft(nextTeamNamesByDate);
      setDraftCounter((n) => n + drafts.length);
    }

    onSave(
      drafts,
      slots,
      isCurrent ? scheduleNotesDraft : summary.notes,
      isCurrent ? pendingPlanDocumentIds : undefined,
      undefined,
      isCurrent ? scheduleTransportasiNoteDraft : summary.transportasiNote,
      {
        stayOnPage: true,
        allowEmptyTeam: drafts.length < 2,
        targetScheduleDocumentId: targetScheduleId,
        replaceRosterOnDate: date,
        teamNamesByDate: nextTeamName ? { [date]: nextTeamName } : { [date]: '' },
      },
    );
  }

  function handleQuotationClick(date: string, summary: QuotationScheduleDaySummary) {
    const isCurrent = summary.scheduleDocumentId === scheduleDocumentId;
    const cancelled = summary.scheduleStatus === 'cancelled';
    const canEditThisCard =
      isCurrent &&
      !locked &&
      !cancelled &&
      (canEditFull || canEditDateOnly);
    if (canEditThisCard) {
      setAssignDialog({
        anchorDate: date,
        weekAnchorDate: defaultSurveyRange?.startDate || date,
        mode: 'edit',
      });
      return;
    }
    openDetailDialog(date, summary);
  }

  const detailSummary = useMemo(() => {
    if (!detailDialog) return null;
    const byDate = new Map<string, QuotationScheduleDaySummary[]>();
    for (const [date, rows] of buildQuotationScheduleDaySummariesByDate(calendarTeams)) {
      byDate.set(date, [...rows]);
    }
    for (const [date, extras] of mergedExtraSummariesByDate) {
      const existing = byDate.get(date) ?? [];
      byDate.set(date, [...existing, ...extras]);
    }
    if (issuedQ.issuedWorkOrderIds.size > 0) {
      for (const [date, rows] of byDate) {
        byDate.set(
          date,
          rows.map((row) => applyBeritaAcaraIssuedFlag(row, issuedQ.issuedWorkOrderIds)),
        );
      }
    }
    const live =
      (byDate.get(detailDialog.date) ?? []).find(
        (row) => row.scheduleDocumentId === detailDialog.summary.scheduleDocumentId,
      ) ?? detailDialog.summary;
    return enrichSummaryForCurrentSchedule(
      live,
      scheduleDocumentId,
      scheduleStatus,
      samplingReceivedAt,
      quotationNumber,
      customerName,
      customerAddress,
      scheduleNotesDraft,
      currentScheduleGroupLabel,
      scheduleTransportasiNoteDraft,
      teamNamesByDateDraft[detailDialog.date] ?? live.teamName,
      workOrderDocumentId,
      workOrderJobNumber,
    );
  }, [
    detailDialog,
    calendarTeams,
    mergedExtraSummariesByDate,
    issuedQ.issuedWorkOrderIds,
    scheduleDocumentId,
    scheduleStatus,
    samplingReceivedAt,
    quotationNumber,
    customerName,
    customerAddress,
    scheduleNotesDraft,
    currentScheduleGroupLabel,
    scheduleTransportasiNoteDraft,
    teamNamesByDateDraft,
    workOrderDocumentId,
    workOrderJobNumber,
  ]);

  async function refreshDetailDialog() {
    if (detailRefreshing || !jwt) return;
    setDetailRefreshing(true);
    try {
      await Promise.all([
        scheduleQ.refetch(),
        issuedQ.refetch(),
        queryClient.refetchQueries({
          queryKey: ['strapi', 'samplingSchedule', jwt, scheduleDocumentId],
        }),
        queryClient.refetchQueries({
          queryKey: ['strapi', 'surveyTeamsBySchedule', jwt, scheduleDocumentId],
        }),
        queryClient.refetchQueries({
          queryKey: ['strapi', 'samplingSchedulesForCalendarOverview', jwt],
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.surveyTeamsSchedule(jwt, null),
        }),
      ]);
    } finally {
      setDetailRefreshing(false);
    }
  }

  const scheduleReadyForWorkOrder = useMemo(
    () =>
      canCreateWorkOrderFromSchedule(
        {
          progress_status: scheduleStatus,
          work_order: null,
          admin_workorder_completed_at: adminWorkorderCompletedAt,
          customer_agreed_start_date: anchorDate,
          customer_agreed_end_date: anchorEndDate,
          customer_agreed_dates: customerAgreedDates,
        },
        user,
      ),
    [
      scheduleStatus,
      adminWorkorderCompletedAt,
      anchorDate,
      anchorEndDate,
      customerAgreedDates,
      user,
    ],
  );
  const detailCanCreateWorkOrder = Boolean(
    detailSummary &&
      detailSummary.scheduleDocumentId === scheduleDocumentId &&
      scheduleReadyForWorkOrder,
  );
  const detailIsCurrentSchedule = detailSummary?.scheduleDocumentId === scheduleDocumentId;
  const detailIsSameGroupedQuotation = Boolean(
    quotationUsesGroups &&
      quotationDocumentId?.trim() &&
      detailSummary &&
      detailSummary.quotationDocumentId.trim() === quotationDocumentId.trim(),
  );
  const detailTargetCancelled = detailSummary?.scheduleStatus === 'cancelled';
  const detailCanEditSchedule = Boolean(
    detailSummary &&
      !detailTargetCancelled &&
      canEditSamplingScheduleDetail(user) &&
      (detailIsCurrentSchedule
        ? !locked && (canEditFull || canEditDateOnly)
        : detailIsSameGroupedQuotation),
  );

  function nextStateAfterRemovingDate(date: string): {
    dateOnlySlots: string[];
    slotsByUserId: Map<string, string[]>;
    teamDrafts: ScheduleTeamMemberDraft[];
  } {
    const nextSlots = removeDateFromSlotsMap(slotsByUserId, date);
    return {
      dateOnlySlots: dateOnlySlots.filter((d) => d !== date),
      slotsByUserId: nextSlots,
      teamDrafts: teamDrafts.filter((draft) => {
        const key = draftMemberKey(draft);
        return Boolean(key && (nextSlots.get(key)?.length ?? 0) > 0);
      }),
    };
  }

  function scheduleCardIsDeletable(summary: QuotationScheduleDaySummary): boolean {
    if ((summary.scheduleStatus ?? '') === 'cancelled') return false;
    if (locked || (!canEditFull && !canEditDateOnly)) return false;
    if (!canEditSamplingScheduleDetail(user)) return false;
    // Kartu biru aktif di halaman detail ini — termasuk setelah WO dibuat
    // (admin sampling / superadmin tetap boleh hapus per tanggal).
    if (isActiveScheduleOnDetailCalendar(summary, scheduleDocumentId)) return true;
    // Jadwal lain pada quotation yang sama (recurring kontrak / group lain)
    // tetap bisa dihapus. Kartu itu bukan jadwal yang sedang dibuka di URL.
    const quotationId = String(quotationDocumentId ?? '').trim();
    const cardQuotationId = String(summary.quotationDocumentId ?? '').trim();
    const cardScheduleId = String(summary.scheduleDocumentId ?? '').trim();
    return Boolean(quotationId && cardScheduleId && cardQuotationId === quotationId);
  }

  /** Hanya kartu biru (jadwal aktif di halaman detail ini) yang boleh di-drag. */
  function scheduleCardIsDraggable(summary: QuotationScheduleDaySummary): boolean {
    if (locked || (!canEditFull && !canEditDateOnly)) return false;
    if ((summary.scheduleStatus ?? '') === 'cancelled') return false;
    // Samakan kriteria visual kartu biru (primary), termasuk setelah WO dibuat
    // selama halaman masih bisa diedit (admin sampling / superadmin).
    return isActiveScheduleOnDetailCalendar(summary, scheduleDocumentId);
  }

  function scheduleCardIsEditable(summary: QuotationScheduleDaySummary): boolean {
    if ((summary.scheduleStatus ?? '') === 'cancelled') return false;
    if (locked || (!canEditFull && !canEditDateOnly)) return false;
    // Hanya kartu biru (jadwal id halaman ini) yang boleh edit.
    return isActiveScheduleOnDetailCalendar(summary, scheduleDocumentId);
  }

  function handleEditScheduleCard(date: string, summary: QuotationScheduleDaySummary) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    if (!scheduleCardIsEditable(summary)) return;
    setAssignDialog({
      anchorDate: date,
      weekAnchorDate: defaultSurveyRange?.startDate || date,
      mode: 'edit',
    });
  }

  function handleViewScheduleCard(date: string, summary: QuotationScheduleDaySummary) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    openDetailDialog(date, summary);
  }

  function handleDeleteScheduleCard(date: string, summary: QuotationScheduleDaySummary) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    if (!scheduleCardIsDeletable(summary)) return;
    deleteScheduleDate(date, summary);
  }

  function handleMoveScheduleCard(
    fromDate: string,
    toDate: string,
    summary: QuotationScheduleDaySummary,
    targetTeam: QuotationScheduleDaySummary,
  ) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate) || !/^\d{4}-\d{2}-\d{2}$/.test(toDate)) return;
    if (!scheduleCardIsDraggable(summary)) return;
    if (!canEditFull) return;
    if (targetTeam.members.length < 2) return;
    if (
      fromDate === toDate &&
      summary.scheduleDocumentId === targetTeam.scheduleDocumentId
    ) {
      return;
    }

    const targetId = summary.scheduleDocumentId.trim();
    const isCurrent = targetId === scheduleDocumentId;

    const draftsFromTargetMembers = (
      members: QuotationScheduleDaySummary['members'],
    ): ScheduleTeamMemberDraft[] => {
      const drafts: ScheduleTeamMemberDraft[] = [];
      let index = 0;
      for (const member of members) {
        const userId = String(member.userId ?? '').trim();
        if (!userId) continue;
        if (isFreelanceMemberKey(userId)) {
          const freelanceDocId = freelanceDocumentIdFromMemberKey(userId);
          const freelanceUser =
            freelanceUsers.find((row) => row.documentId === freelanceDocId) ?? null;
          if (!freelanceUser) continue;
          drafts.push({
            draftKey: `adopt-${userId}-${index}`,
            user: null,
            freelanceUser,
            is_lead: Boolean(member.isLead),
          });
          index += 1;
          continue;
        }
        const user = users.find((row) => row.id === userId) ?? null;
        if (!user) continue;
        drafts.push({
          draftKey: `adopt-${userId}-${index}`,
          user,
          freelanceUser: null,
          is_lead: Boolean(member.isLead),
        });
        index += 1;
      }
      return drafts;
    };

    const mergeDraftsForSlots = (
      previousDrafts: ScheduleTeamMemberDraft[],
      adoptedDrafts: ScheduleTeamMemberDraft[],
      nextSlots: Map<string, string[]>,
    ): ScheduleTeamMemberDraft[] => {
      const byKey = new Map<string, ScheduleTeamMemberDraft>();
      for (const draft of previousDrafts) {
        const key = draftMemberKey(draft);
        if (!key || (nextSlots.get(key)?.length ?? 0) === 0) continue;
        byKey.set(key, draft);
      }
      for (const draft of adoptedDrafts) {
        const key = draftMemberKey(draft);
        if (!key || (nextSlots.get(key)?.length ?? 0) === 0) continue;
        if (!byKey.has(key)) byKey.set(key, draft);
      }
      const merged = [...byKey.values()];
      if (merged.length > 0 && !merged.some((draft) => draft.is_lead)) {
        merged[0] = { ...merged[0], is_lead: true };
      } else if (merged.some((draft) => draft.is_lead)) {
        let leadAssigned = false;
        for (let i = 0; i < merged.length; i += 1) {
          const isLead = Boolean(merged[i].is_lead) && !leadAssigned;
          merged[i] = { ...merged[i], is_lead: isLead };
          if (isLead) leadAssigned = true;
        }
      }
      return merged;
    };

    if (isCurrent) {
      const adoptedDrafts = draftsFromTargetMembers(targetTeam.members);
      if (adoptedDrafts.length < 2) return;
      const nextSlots = buildSlotsAdoptingTeamOnSingleDate({
        previousSlots: slotsByUserId,
        fromDate,
        toDate,
        teamMembers: targetTeam.members.map((member) => ({
          userId: member.userId,
          timeLabels: member.timeLabels,
        })),
      });
      const nextDrafts = mergeDraftsForSlots(teamDrafts, adoptedDrafts, nextSlots);
      if (nextDrafts.length < 2) return;
      const nextDateOnly = dateOnlySlots.filter((date) => date !== fromDate).sort();
      setDateOnlySlots(nextDateOnly);
      setSlotsByUserId(nextSlots);
      setTeamDrafts(nextDrafts);
      persistDeletedDate(
        nextDrafts,
        nextSlots,
        scheduleNotesDraft,
        nextDateOnly,
        scheduleTransportasiNoteDraft,
        pendingQuotationGroupDocumentId || undefined,
        // Jadwal yang sedang dibuka. Jangan buat jadwal baru lewat ensure:
        // kontrak yang sudah punya work order dan index recurring akan ter-clone.
        scheduleDocumentId,
        pendingPlanDocumentIds,
      );
      return;
    }

    // Drop untuk jadwal sibling (group quotation yang sama): adopt tim di 1 tanggal saja.
    const groupId = quotationGroupIdForSchedule(
      allSchedulesForCalendar,
      calendarTeams,
      targetId,
    );
    const { drafts, slotsByUserId: siblingSlots } = collectTeamDraftStateFromRows(
      calendarTeams,
      targetId,
      users,
      freelanceUsers,
    );
    const adoptedDrafts = draftsFromTargetMembers(targetTeam.members);
    if (adoptedDrafts.length < 2) {
      // Fallback: hanya pindah tanggal jika roster target tidak bisa di-resolve.
      if (drafts.length === 0) {
        const remainingDates = remainingDateOnlySlotsForSchedule(
          mergedExtraSummariesByDate,
          allSchedulesForCalendar,
          targetId,
          fromDate,
        );
        const dateOnlyForSave = [...new Set([...remainingDates, toDate])].sort();
        persistDeletedDate(
          [],
          new Map(),
          summary.notes,
          dateOnlyForSave,
          summary.transportasiNote,
          groupId || undefined,
          targetId,
        );
        return;
      }
      const nextSlots = moveDateInSlotsMap(siblingSlots, fromDate, toDate);
      const keptDrafts = drafts.filter((draft) => {
        const key = draftMemberKey(draft);
        return Boolean(key && (nextSlots.get(key)?.length ?? 0) > 0);
      });
      persistDeletedDate(
        keptDrafts,
        nextSlots,
        summary.notes,
        undefined,
        summary.transportasiNote,
        groupId || undefined,
        targetId,
      );
      return;
    }

    const nextSlots = buildSlotsAdoptingTeamOnSingleDate({
      previousSlots: siblingSlots,
      fromDate,
      toDate,
      teamMembers: targetTeam.members.map((member) => ({
        userId: member.userId,
        timeLabels: member.timeLabels,
      })),
    });
    const nextDrafts = mergeDraftsForSlots(drafts, adoptedDrafts, nextSlots);
    // Sisa tanggal kosong sibling tidak di-persist sebagai rentang customer_agreed
    // (hanya tanggal tim yang di-drag yang tersimpan lewat slots).
    persistDeletedDate(
      nextDrafts,
      nextSlots,
      summary.notes,
      undefined,
      summary.transportasiNote,
      groupId || undefined,
      targetId,
    );
  }

  function persistDeletedDate(
    members: ScheduleTeamMemberDraft[],
    slots: Map<string, string[]>,
    notes: string,
    dateOnly: string[] | undefined,
    transportasiNote: string,
    groupId: string | undefined,
    targetScheduleDocumentId: string | undefined,
    planDocumentIds?: string[],
  ) {
    const remainingAssigned = members.filter((draft) => {
      const key = draftMemberKey(draft);
      return Boolean(key && (slots.get(key)?.length ?? 0) > 0);
    }).length;
    // Pastikan Map tidak “hilang” saat dioper ke mutation.
    const slotsForSave = new Map(
      [...slots.entries()].map(([key, value]) => [String(key), [...value]]),
    );
    onSave(members, slotsForSave, notes, planDocumentIds, dateOnly, transportasiNote, {
      stayOnPage: true,
      allowEmptyTeam: remainingAssigned < 2,
      quotationGroupDocumentId: groupId,
      targetScheduleDocumentId,
    });
  }

  function deleteScheduleDate(date: string, summary: QuotationScheduleDaySummary) {
    const targetId = summary.scheduleDocumentId.trim() || scheduleDocumentId;
    const isCurrent = targetId === scheduleDocumentId;
    const isTeamCard = summary.members.length > 0;
    const hideKey = scheduleDateHideKey(targetId, date);
    setHiddenScheduleDateKeys((prev) => {
      if (prev.has(hideKey)) return prev;
      const next = new Set(prev);
      next.add(hideKey);
      return next;
    });
    const unhide = () => {
      setHiddenScheduleDateKeys((prev) => {
        if (!prev.has(hideKey)) return prev;
        const next = new Set(prev);
        next.delete(hideKey);
        return next;
      });
    };
    if (
      detailDialog?.date === date &&
      detailDialog.summary.scheduleDocumentId.trim() === targetId
    ) {
      setDetailDialog(null);
    }

    if (isTeamCard && onRemoveScheduleDate) {
      if (isCurrent) {
        const loaded = collectTeamDraftStateFromRows(
          teams,
          scheduleDocumentId,
          users,
          freelanceUsers,
        );
        const sourceSlots =
          loaded.slotsByUserId.size > 0 ? loaded.slotsByUserId : slotsByUserId;
        const sourceDrafts = loaded.drafts.length > 0 ? loaded.drafts : teamDrafts;
        const nextSlots = removeDateFromSlotsMap(sourceSlots, date);
        const nextDrafts = sourceDrafts.filter((draft) => {
          const key = draftMemberKey(draft);
          return Boolean(key && (nextSlots.get(key)?.length ?? 0) > 0);
        });
        const nextDateOnly = dateOnlySlots.filter((d) => d !== date);
        setDateOnlySlots(nextDateOnly);
        setSlotsByUserId(nextSlots);
        setTeamDrafts(nextDrafts);
      }
      void Promise.resolve(
        onRemoveScheduleDate({
          date,
          scheduleDocumentId: targetId,
          notes: isCurrent ? scheduleNotesDraft : summary.notes,
          transportasiNote: isCurrent ? scheduleTransportasiNoteDraft : summary.transportasiNote,
        }),
      ).catch(() => unhide());
      return;
    }

    if (onRemoveScheduleDate) {
      if (isCurrent) {
        const next = nextStateAfterRemovingDate(date);
        setDateOnlySlots(next.dateOnlySlots);
        if (next.dateOnlySlots.length === 0) {
          setPendingPlanDocumentIds([]);
          setPendingQuotationGroupDocumentId('');
        }
        setSlotsByUserId(next.slotsByUserId);
        setTeamDrafts(next.teamDrafts);
      }
      void Promise.resolve(
        onRemoveScheduleDate({
          date,
          scheduleDocumentId: targetId,
          notes: isCurrent ? scheduleNotesDraft : summary.notes,
          transportasiNote: isCurrent ? scheduleTransportasiNoteDraft : summary.transportasiNote,
        }),
      ).catch(() => unhide());
      return;
    }

    if (isCurrent) {
      const next = nextStateAfterRemovingDate(date);
      setDateOnlySlots(next.dateOnlySlots);
      if (next.dateOnlySlots.length === 0) {
        setPendingPlanDocumentIds([]);
        setPendingQuotationGroupDocumentId('');
      }
      setSlotsByUserId(next.slotsByUserId);
      setTeamDrafts(next.teamDrafts);
      persistDeletedDate(
        next.teamDrafts,
        next.slotsByUserId,
        scheduleNotesDraft,
        next.dateOnlySlots,
        scheduleTransportasiNoteDraft,
        pendingQuotationGroupDocumentId || undefined,
        undefined,
        pendingPlanDocumentIds,
      );
      return;
    }

    const groupId = quotationGroupIdForSchedule(
      allSchedulesForCalendar,
      calendarTeams,
      targetId,
    );
    const { drafts, slotsByUserId: siblingSlots } = collectTeamDraftStateFromRows(
      calendarTeams,
      targetId,
      users,
      freelanceUsers,
    );
    const hasTeamSlots = drafts.some((draft) => {
      const key = draftMemberKey(draft);
      return Boolean(key && (siblingSlots.get(key)?.length ?? 0) > 0);
    });

    if (hasTeamSlots) {
      const nextSlots = removeDateFromSlotsMap(siblingSlots, date);
      const nextDrafts = drafts.filter((draft) => {
        const key = draftMemberKey(draft);
        return Boolean(key && (nextSlots.get(key)?.length ?? 0) > 0);
      });
      persistDeletedDate(
        nextDrafts,
        nextSlots,
        summary.notes,
        undefined,
        summary.transportasiNote,
        groupId || undefined,
        targetId,
      );
      return;
    }

    const remainingDates = remainingDateOnlySlotsForSchedule(
      mergedExtraSummariesByDate,
      allSchedulesForCalendar,
      targetId,
      date,
    );
    persistDeletedDate(
      [],
      new Map(),
      summary.notes,
      remainingDates,
      summary.transportasiNote,
      groupId || undefined,
      targetId,
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {canEditDateOnly
          ? 'Admin WO mengatur tanggal dan work order di modal, lalu klik Simpan tanggal & work order. Petugas sampling diisi kemudian oleh admin sampling.'
          : canEditFull
            ? hasCurrentQuotationSchedule
              ? 'Klik ikon edit di kartu untuk mengubah tanggal/jam. Seret kartu biru ke kartu/slot tim yang sudah ada untuk memindahkan tanggal sekaligus memakai tim survey tersebut. Ikon hapus menghapus jadwal pada tanggal tersebut.'
              : 'Work order boleh sudah ada. Klik + pada tanggal untuk mengatur tanggal. Seret kartu biru ke kartu/slot tim yang sudah ada untuk menugaskan petugas. Simpan di modal langsung tersimpan.'
            : hasCurrentQuotationSchedule || hasDateOnlySchedule
              ? 'Jadwal sudah dibuat. Klik kartu untuk melihat detail.'
              : 'Belum ada jadwal pada quotation ini.'}
      </Typography>

      <SurveyScheduleCalendarToolbar
        viewMode="monthly"
        onViewModeChange={() => undefined}
        weekStart=""
        onWeekStartChange={() => undefined}
        monthAnchor={monthAnchor}
        onMonthAnchorChange={setMonthAnchor}
        weekDates={[]}
        hideViewModeToggle
      />

      <SurveyScheduleCalendarLegend mode="monthly" variant="received-detail" />

      {assignSaveError ? (
        <Alert severity="error" onClose={() => setAssignSaveError(null)}>
          {assignSaveError}
        </Alert>
      ) : null}

      {scheduleQ.isPending || issuedQ.isPending ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : scheduleQ.isError || issuedQ.isError ? (
        <Alert severity="error">
          {scheduleQ.error instanceof Error
            ? scheduleQ.error.message
            : issuedQ.error instanceof Error
              ? issuedQ.error.message
              : 'Gagal memuat kalender.'}
        </Alert>
      ) : (
        <MonthlyQuotationTeamCalendar
          monthAnchor={monthAnchor}
          teams={visibleCalendarTeams}
          extraSummariesByDate={visibleExtraSummariesByDate}
          currentScheduleDocumentId={scheduleDocumentId}
          cardTone="detail"
          useScheduleStatusColors={false}
          issuedWorkOrderIds={issuedQ.issuedWorkOrderIds}
          locked={locked}
          rootTestId="schedule-team-view-monthly"
          onQuotationClick={handleQuotationClick}
          onAddClick={
            locked || !canAddSlot
              ? undefined
              : (date) =>
                  setAssignDialog({
                    anchorDate: date,
                    weekAnchorDate: defaultSurveyRange?.startDate || date,
                    mode: canEditDateOnly && hasDateOnlySchedule ? 'edit' : 'add',
                  })
          }
          canDeleteSchedule={scheduleCardIsDeletable}
          canEditSchedule={scheduleCardIsEditable}
          canDragSchedule={(summary) => canEditFull && scheduleCardIsDraggable(summary)}
          onScheduleCardDrop={
            locked || !canEditFull
              ? undefined
              : (fromDate, toDate, summary, targetTeam) => {
                  handleMoveScheduleCard(fromDate, toDate, summary, targetTeam);
                }
          }
          onEditScheduleClick={(date, summary) => {
            handleEditScheduleCard(date, summary);
          }}
          onViewScheduleClick={(date, summary) => {
            handleViewScheduleCard(date, summary);
          }}
          onDeleteScheduleClick={(date, summary) => {
            handleDeleteScheduleCard(date, summary);
          }}
          teamOfficerOptions={userCandidates.map((row) => ({
            userId: row.id,
            name: row.name,
          }))}
          canEditTeam={scheduleCardIsEditable}
          onSaveTeamMembers={
            locked || !canEditFull
              ? undefined
              : (date, summary, payload) => {
                  handleSaveTeamMembers(date, summary, payload);
                }
          }
          teamMembersSaving={saving}
          getDayTestId={(date) => `schedule-team-day-${date}`}
          getAddTestId={(date) => `schedule-team-add-${date}`}
        />
      )}

      {scheduleReadyForWorkOrder && quotationDocumentId ? (
        <Button
          variant="contained"
          color="primary"
          sx={{ alignSelf: 'flex-start' }}
          data-testid="schedule-create-work-order"
          onClick={() =>
            navigate(workOrderNewPathFromSchedule(quotationDocumentId, scheduleDocumentId))
          }
        >
          Buat work order
        </Button>
      ) : null}

      {detailDialog ? (
        <QuotationScheduleDayDetailDialog
          open
          date={detailDialog.date}
          summary={detailSummary}
          onClose={() => setDetailDialog(null)}
          onRefresh={() => void refreshDetailDialog()}
          refreshing={detailRefreshing}
          canCreateWorkOrder={detailCanCreateWorkOrder}
          onCreateWorkOrder={() => {
            if (!detailSummary || !quotationDocumentId) return;
            navigate(
              workOrderNewPathFromSchedule(quotationDocumentId, detailSummary.scheduleDocumentId),
            );
          }}
          onOpenSchedule={
            detailSummary &&
            detailSummary.scheduleDocumentId.trim() &&
            detailSummary.scheduleDocumentId.trim() !== scheduleDocumentId
              ? () => {
                  const targetId = detailSummary.scheduleDocumentId.trim();
                  setDetailDialog(null);
                  navigate(`/sampling/schedules/${encodeURIComponent(targetId)}`);
                }
              : undefined
          }
          canEditSchedule={detailCanEditSchedule}
          onEditSchedule={() => {
            if (!detailDialog || !detailSummary) return;
            const targetId = detailSummary.scheduleDocumentId.trim();
            if (targetId && targetId !== scheduleDocumentId) {
              setDetailDialog(null);
              navigate(`/sampling/schedules/${encodeURIComponent(targetId)}`, {
                state: { editScheduleDate: detailDialog.date },
              });
              return;
            }
            setDetailDialog(null);
            setAssignDialog({
              anchorDate: detailDialog.date,
              weekAnchorDate: defaultSurveyRange?.startDate || detailDialog.date,
              mode: 'edit',
            });
          }}
        />
      ) : null}

      {assignDialog ? (
        <QuotationScheduleSlotAssignDialog
          open
          anchorDate={assignDialog.anchorDate}
          weekAnchorDate={assignDialog.weekAnchorDate}
          onWeekAnchorDateChange={(date) =>
            setAssignDialog((prev) => (prev ? { ...prev, weekAnchorDate: date } : prev))
          }
          quotationLabel={quotationLabel}
          availability={assignAvailability}
          weekAvailabilityByUserId={weekAvailabilityByUserId}
          mode={assignDialog.mode}
          assignmentMode={canEditDateOnly ? 'date_only' : 'team'}
          datePickMode={
            quotationGroupMeta?.is_contract || quotationGroupMeta?.is_consultant_quote
              ? 'toggle'
              : canEditDateOnly
                ? 'range'
                : 'toggle'
          }
          canCreateWorkOrder={canEditDateOnly && !locked}
          initialEntries={assignDialog.mode === 'edit' ? editInitialEntries : []}
          initialDateOnlyRange={
            defaultSurveyRange
              ? {
                  ...defaultSurveyRange,
                  dates: slotModalDates
                    .map((date) => String(date ?? '').trim().slice(0, 10))
                    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
                    .sort(),
                }
              : null
          }
          initialGroupDocumentId={
            quotationGroupMeta?.is_contract
              ? (sampleGroupOptions[0]
                ? sampleGroupOptionSelectionKey(sampleGroupOptions[0])
                : null)
              : sampleGroupOptions.find((option) => option.scheduleDocumentId && option.scheduleDocumentId === scheduleDocumentId)
                ?.selectionKey
                || currentScheduleGroupDocumentId
                || null
          }
          pinSampleGroupInEdit={
            Boolean(quotationGroupMeta?.is_consultant_quote) && !quotationGroupMeta?.is_contract
          }
          pinSampleGroup={Boolean(quotationGroupMeta?.is_contract)}
          scheduleNotes={scheduleNotesDraft}
          scheduleTransportasiNote={scheduleTransportasiNoteDraft}
          initialTeamNamesByDate={teamNamesByDateDraft}
          canEditTransportasiNote={canEditTransportasiNote}
          locked={locked}
          requiresSampleGroupSelection={quotationUsesGroups}
          sampleGroupOptions={sampleGroupOptions}
          sampleGroupsLoading={sampleGroupsLoading}
          teamSuggestions={[]}
          canAddFreelanceUser={false}
          onCreateFreelanceUser={undefined}
          onClose={() => setAssignDialog(null)}
          onConfirm={(payloads, notesMeta, dateOnlySelection, workOrder) =>
            handleSlotAssignConfirm(
              payloads,
              notesMeta,
              dateOnlySelection,
              workOrder,
              assignDialog.mode === 'edit',
            )
          }
        />
      ) : null}

      {!locked && canPersistSchedule ? (
        <Stack spacing={0.75} sx={{ alignSelf: 'flex-start' }}>
          <Button
            size="small"
            variant="contained"
            data-testid="schedule-save-team"
            onClick={() =>
              onSave(
                teamDrafts,
                slotsByUserId,
                scheduleNotesDraft,
                pendingPlanDocumentIds,
                canEditDateOnly || canEditFull ? dateOnlySlots : undefined,
                scheduleTransportasiNoteDraft,
                {
                  quotationGroupDocumentId: pendingQuotationGroupDocumentId || undefined,
                },
              )
            }
            disabled={saving || !canSaveFullTeam}
            sx={{ alignSelf: 'flex-start' }}
          >
            {canEditDateOnly ? 'Simpan tanggal penjadwalan' : 'Simpan tim survey'}
          </Button>
          {canEditFull && !canSaveFullTeam ? (
            <Typography variant="caption" color="warning.main">
              Tim sampling minimal harus terdiri dari 2 petugas.
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}
